import { useState, useEffect, useCallback } from "react";
import { API_BASE, UPLOADS_BASE, MEDICAL_SPECIALTY_TYPE_OPTIONS } from "../constants/api";
import {
  STEPS, STEP_DESCRIPTIONS, MENU_GROUPS, getGroupSteps, LOGIN_ADMIN_STEPS, isLoginAdminStep, DASHBOARD_STEP,
} from "../constants/menuConfig";
import {
  parseImagePaths, parseApiError, formatDateTime,
  formatProcCostUsd, formatProcCostInr, buildEstablishmentFormData, buildDoctorFormData, buildPatientFormData, buildUserFormData,
} from "../utils/helpers";
import { generateSystemPassword } from "../utils/loginHelpers";
import { isLocationUnderParent, resolveCountryFieldsFromParent } from "../../../utils/locationHelpers";
import {
  UserTypeMasterForm,
  LocationTypeMasterForm,
  LocationMasterForm,
  MedicalSpecialtyForm,
  MedicalProcedureForm,
  DiseaseForm,
  SymptomCategoryForm,
  SymptomForm,
  AggravatingFactorForm,
  AllergyTypeForm,
  AllergyForm,
  TraumaTypeForm,
  TraumaForm,
  AddictionCategoryForm,
  AddictionSubCategoryForm,
  AddictionsForm,
  PatientConditionForm,
  InvestigationTypeForm,
  InvestigationGroupForm,
  InvestigationForm,
  EstablishmentTypeForm,
  EstablishmentSubTypeForm,
  MedicalDegreeForm,
  SpecializationForm,
  SpecializationTypeForm,
  AccreditationForm,
  DepartmentCategoryForm,
  DepartmentMasterForm,
  DesignationForm,
  RelationshipForm,
  BloodGroupForm,
  TherapeuticClassForm,
  MedicineSaltForm,
  DosageForm,
  MedicalPrescriptionForm,
  PatientPrescriptionMedicineForm,
  PatientCaseForm,
  PatientComplaintForm,
  CaseDiagnosisForm,
  PatientInvestigationForm,
  appendPrescriptionFormData,
  appendInvestigationFormData,
  ProfileTopicForm,
  ProfileSubTopicForm,
  ServiceTypeForm,
  MedicalServiceForm,
  ServiceForm,
  QuestionTypeForm,
  QuestionForm,
  OptionForm,
  QuestionGroupForm,
  EventTypeForm,
  ImmunizationForm,
  VaccineForm,
  getMorbidityDiseases,
  appendQuestionFormData,
  SupplierTypeForm,
  ProductCategoryForm,
  ProductSubCategoryForm,
  ProductMasterForm,
  MvtJourneyPhaseForm,
  MvtJourneyStepForm,
  appendProductFormData,
  PRODUCT_STATUS_OPTIONS,
} from "../forms";
import EstablishmentProfileForm from "../forms/EstablishmentProfileForm";
import DoctorProfileForm from "../forms/DoctorProfileForm";
import { mapEstablishmentToProfileForm } from "../utils/establishmentFormHelpers";
import { mapDoctorToProfileForm } from "../utils/doctorFormHelpers";
import { mapPatientToForm, emptyPatientForm } from "../utils/patientFormHelpers";
import { mapUserToForm, emptyUserForm } from "../utils/userFormHelpers";
import PatientMasterForm from "../forms/PatientMasterForm";
import UserMasterForm from "../forms/UserMasterForm";

export function useAdminDashboardState() {
  const [currentStep, setCurrentStep] = useState(DASHBOARD_STEP);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expandedSections, setExpandedSections] = useState({});
  const [validated, setValidated] = useState(false);
  const [userTypeForm, setUserTypeForm] = useState({});
  const [userTypes, setUserTypes] = useState([]);
  const [locationTypes, setLocationTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [locationTypeForm, setLocationTypeForm] = useState({});
  const [locationForm, setLocationForm] = useState({});
  const [specialtyForm, setSpecialtyForm] = useState({});
  const [estTypeForm, setEstTypeForm] = useState({});
  const [specialties, setSpecialties] = useState([]);
  const [medicalProcedureForm, setMedicalProcedureForm] = useState({});
  const [medicalProcedures, setMedicalProcedures] = useState([]);
  const [diseaseForm, setDiseaseForm] = useState({});
  const [diseases, setDiseases] = useState([]);
  const [symptomCategoryForm, setSymptomCategoryForm] = useState({});
  const [symptomCategories, setSymptomCategories] = useState([]);
  const [symptomForm, setSymptomForm] = useState({});
  const [symptoms, setSymptoms] = useState([]);
  const [aggravatingFactorForm, setAggravatingFactorForm] = useState({});
  const [aggravatingFactors, setAggravatingFactors] = useState([]);
  const [allergyTypeForm, setAllergyTypeForm] = useState({});
  const [allergyTypes, setAllergyTypes] = useState([]);
  const [allergyForm, setAllergyForm] = useState({});
  const [allergies, setAllergies] = useState([]);
  const [traumaTypeForm, setTraumaTypeForm] = useState({});
  const [traumaTypes, setTraumaTypes] = useState([]);
  const [traumaForm, setTraumaForm] = useState({});
  const [traumas, setTraumas] = useState([]);
  const [addictionCategoryForm, setAddictionCategoryForm] = useState({});
  const [addictionCategories, setAddictionCategories] = useState([]);
  const [addictionSubCategoryForm, setAddictionSubCategoryForm] = useState({});
  const [addictionSubCategories, setAddictionSubCategories] = useState([]);
  const [addictionsForm, setAddictionsForm] = useState({});
  const [addictionsList, setAddictionsList] = useState([]);
  const [patientConditionForm, setPatientConditionForm] = useState({});
  const [patientConditions, setPatientConditions] = useState([]);
  const [investigationTypeForm, setInvestigationTypeForm] = useState({});
  const [investigationTypes, setInvestigationTypes] = useState([]);
  const [investigationGroupForm, setInvestigationGroupForm] = useState({});
  const [investigationGroups, setInvestigationGroups] = useState([]);
  const [investigationForm, setInvestigationForm] = useState({});
  const [investigations, setInvestigations] = useState([]);
  const [estTypes, setEstTypes] = useState([]);
  const [establishmentSubTypeForm, setEstablishmentSubTypeForm] = useState({});
  const [establishmentSubTypes, setEstablishmentSubTypes] = useState([]);
  const [degreeForm, setDegreeForm] = useState({});
  const [specializationForm, setSpecializationForm] = useState({});
  const [degrees, setDegrees] = useState([]);
  const [specializations, setSpecializations] = useState([]);
  const [specializationTypeForm, setSpecializationTypeForm] = useState({});
  const [specializationTypes, setSpecializationTypes] = useState([]);
  const [accreditationForm, setAccreditationForm] = useState({});
  const [accreditations, setAccreditations] = useState([]);
  const [departmentCategoryForm, setDepartmentCategoryForm] = useState({});
  const [departmentCategories, setDepartmentCategories] = useState([]);
  const [departmentForm, setDepartmentForm] = useState({});
  const [departments, setDepartments] = useState([]);
  const [designationForm, setDesignationForm] = useState({});
  const [designations, setDesignations] = useState([]);
  const [relationshipForm, setRelationshipForm] = useState({});
  const [relationships, setRelationships] = useState([]);
  const [bloodGroupForm, setBloodGroupForm] = useState({});
  const [bloodGroups, setBloodGroups] = useState([]);
  const [therapeuticClassForm, setTherapeuticClassForm] = useState({});
  const [therapeuticClasses, setTherapeuticClasses] = useState([]);
  const [medicineSaltForm, setMedicineSaltForm] = useState({});
  const [medicineSalts, setMedicineSalts] = useState([]);
  const [medicalPrescriptionForm, setMedicalPrescriptionForm] = useState({});
  const [medicalPrescriptions, setMedicalPrescriptions] = useState([]);
  const [medicineDosageForm, setMedicineDosageForm] = useState({});
  const [medicineDosages, setMedicineDosages] = useState([]);
  const [patientPrescriptionMedicineForm, setPatientPrescriptionMedicineForm] = useState({
    is_empty_stomach: 0,
    is_sos: 0,
  });
  const [patientPrescriptionMedicines, setPatientPrescriptionMedicines] = useState([]);
  const [patientCases, setPatientCases] = useState([]);
  const [patientCaseForm, setPatientCaseForm] = useState({});
  const [patientComplaints, setPatientComplaints] = useState([]);
  const [patientComplaintForm, setPatientComplaintForm] = useState({ is_sos: false, aggravating_factor_ids: [], aggravating_factors: [] });
  const [caseDiagnoses, setCaseDiagnoses] = useState([]);
  const [caseDiagnosisForm, setCaseDiagnosisForm] = useState({ status: "Provisional" });
  const [patientInvestigations, setPatientInvestigations] = useState([]);
  const [patientInvestigationForm, setPatientInvestigationForm] = useState({});
  const [supplierTypeForm, setSupplierTypeForm] = useState({});
  const [supplierTypes, setSupplierTypes] = useState([]);
  const [productCategoryForm, setProductCategoryForm] = useState({ status: "Active" });
  const [productCategories, setProductCategories] = useState([]);
  const [productSubCategoryForm, setProductSubCategoryForm] = useState({ status: "Active" });
  const [productSubCategories, setProductSubCategories] = useState([]);
  const [productForm, setProductForm] = useState({ status: "Active" });
  const [products, setProducts] = useState([]);
  const [mvtJourneyPhaseForm, setMvtJourneyPhaseForm] = useState({});
  const [mvtJourneyPhases, setMvtJourneyPhases] = useState([]);
  const [mvtJourneyStepForm, setMvtJourneyStepForm] = useState({});
  const [mvtJourneySteps, setMvtJourneySteps] = useState([]);
  const [profileTopicForm, setProfileTopicForm] = useState({});
  const [profileTopics, setProfileTopics] = useState([]);
  const [profileSubTopicForm, setProfileSubTopicForm] = useState({});
  const [profileSubTopics, setProfileSubTopics] = useState([]);
  const [serviceTypeForm, setServiceTypeForm] = useState({});
  const [serviceTypes, setServiceTypes] = useState([]);
  const [medicalServiceForm, setMedicalServiceForm] = useState({});
  const [medicalServices, setMedicalServices] = useState([]);
  const [serviceForm, setServiceForm] = useState({});
  const [services, setServices] = useState([]);
  const [questionTypeForm, setQuestionTypeForm] = useState({});
  const [questionTypes, setQuestionTypes] = useState([]);
  const [questionForm, setQuestionForm] = useState({});
  const [questions, setQuestions] = useState([]);
  const [optionForm, setOptionForm] = useState({});
  const [options, setOptions] = useState([]);
  const [questionGroupForm, setQuestionGroupForm] = useState({});
  const [questionGroups, setQuestionGroups] = useState([]);
  const [establishmentForm, setEstablishmentForm] = useState({});
  const [establishments, setEstablishments] = useState([]);
  const [doctorForm, setDoctorForm] = useState({});
  const [doctors, setDoctors] = useState([]);
  const [patientForm, setPatientForm] = useState(emptyPatientForm());
  const [patients, setPatients] = useState([]);
  const [userForm, setUserForm] = useState(emptyUserForm());
  const [users, setUsers] = useState([]);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingPatientId, setEditingPatientId] = useState(null);
  const [eventTypeForm, setEventTypeForm] = useState({});
  const [eventTypes, setEventTypes] = useState([]);
  const [immunizationForm, setImmunizationForm] = useState({});
  const [immunizations, setImmunizations] = useState([]);
  const [vaccineForm, setVaccineForm] = useState({});
  const [vaccines, setVaccines] = useState([]);
  const [loginLogs, setLoginLogs] = useState([]);
  const [createLoginEstb, setCreateLoginEstb] = useState(null);
  const [createLoginDoctor, setCreateLoginDoctor] = useState(null);
  const [establishmentLoginForm, setEstablishmentLoginForm] = useState({});
  const [establishmentLogins, setEstablishmentLogins] = useState([]);
  const [doctorLoginForm, setDoctorLoginForm] = useState({});
  const [doctorLogins, setDoctorLogins] = useState([]);
  const [locationLoginForm, setLocationLoginForm] = useState({});
  const [locationLogins, setLocationLogins] = useState([]);
  const [loginPasswordsById, setLoginPasswordsById] = useState({});
  const [savingEstablishmentLogin, setSavingEstablishmentLogin] = useState(false);
  const [savingDoctorLogin, setSavingDoctorLogin] = useState(false);
  const [savingLocationLogin, setSavingLocationLogin] = useState(false);
  const [createLoginUser, setCreateLoginUser] = useState(null);
  const [userLoginForm, setUserLoginForm] = useState({});
  const [userLogins, setUserLogins] = useState([]);
  const [savingUserLogin, setSavingUserLogin] = useState(false);
  const [editingEstbId, setEditingEstbId] = useState(null);
  const [editingEstbData, setEditingEstbData] = useState({});
  const [editingDoctorId, setEditingDoctorId] = useState(null);
  const [editingDoctorData, setEditingDoctorData] = useState({});
  const [submitMsg, setSubmitMsg] = useState("");

  // Edit state
  const [editingUserTypeId, setEditingUserTypeId] = useState(null);
  const [editingUserTypeValue, setEditingUserTypeValue] = useState("");
  const [editingTypeId, setEditingTypeId] = useState(null);
  const [editingTypeValue, setEditingTypeValue] = useState("");
  const [editingLocId, setEditingLocId] = useState(null);
  const [editingLocData, setEditingLocData] = useState({});
  const [editingSpecId, setEditingSpecId] = useState(null);
  const [editingSpecData, setEditingSpecData] = useState({});
  const [editingProcId, setEditingProcId] = useState(null);
  const [editingProcData, setEditingProcData] = useState({});
  const [editingDiseaseId, setEditingDiseaseId] = useState(null);
  const [editingDiseaseData, setEditingDiseaseData] = useState({});
  const [editingSymptomId, setEditingSymptomId] = useState(null);
  const [editingSymptomData, setEditingSymptomData] = useState({});
  const [editingAggravatingFactorId, setEditingAggravatingFactorId] = useState(null);
  const [editingAggravatingFactorValue, setEditingAggravatingFactorValue] = useState("");
  const [editingAllergyTypeId, setEditingAllergyTypeId] = useState(null);
  const [editingAllergyTypeValue, setEditingAllergyTypeValue] = useState("");
  const [editingAllergyId, setEditingAllergyId] = useState(null);
  const [editingAllergyData, setEditingAllergyData] = useState({});
  const [editingTraumaTypeId, setEditingTraumaTypeId] = useState(null);
  const [editingTraumaTypeValue, setEditingTraumaTypeValue] = useState("");
  const [editingTraumaId, setEditingTraumaId] = useState(null);
  const [editingTraumaData, setEditingTraumaData] = useState({});
  const [editingAddictionCategoryId, setEditingAddictionCategoryId] = useState(null);
  const [editingAddictionCategoryValue, setEditingAddictionCategoryValue] = useState("");
  const [editingAddictionSubCategoryId, setEditingAddictionSubCategoryId] = useState(null);
  const [editingAddictionSubCategoryData, setEditingAddictionSubCategoryData] = useState({});
  const [editingAddictionId, setEditingAddictionId] = useState(null);
  const [editingAddictionData, setEditingAddictionData] = useState({});
  const [editingPatientConditionId, setEditingPatientConditionId] = useState(null);
  const [editingPatientConditionValue, setEditingPatientConditionValue] = useState("");
  const [editingInvestigationTypeId, setEditingInvestigationTypeId] = useState(null);
  const [editingInvestigationTypeValue, setEditingInvestigationTypeValue] = useState("");
  const [editingInvestigationGroupId, setEditingInvestigationGroupId] = useState(null);
  const [editingInvestigationGroupData, setEditingInvestigationGroupData] = useState({});
  const [editingInvestigationId, setEditingInvestigationId] = useState(null);
  const [editingInvestigationData, setEditingInvestigationData] = useState({});
  const [editingEstId, setEditingEstId] = useState(null);
  const [editingEstValue, setEditingEstValue] = useState("");
  const [editingEstSubTypeId, setEditingEstSubTypeId] = useState(null);
  const [editingEstSubTypeData, setEditingEstSubTypeData] = useState({});
  const [editingDegId, setEditingDegId] = useState(null);
  const [editingDegValue, setEditingDegValue] = useState("");
  const [editingSpeczId, setEditingSpeczId] = useState(null);
  const [editingSpeczData, setEditingSpeczData] = useState({});
  const [editingSpecTypeId, setEditingSpecTypeId] = useState(null);
  const [editingSpecTypeValue, setEditingSpecTypeValue] = useState("");
  const [editingAccrId, setEditingAccrId] = useState(null);
  const [editingAccrValue, setEditingAccrValue] = useState("");
  const [editingDeptCatId, setEditingDeptCatId] = useState(null);
  const [editingDeptCatValue, setEditingDeptCatValue] = useState("");
  const [editingDeptId, setEditingDeptId] = useState(null);
  const [editingDeptData, setEditingDeptData] = useState({});
  const [editingDesigId, setEditingDesigId] = useState(null);
  const [editingDesigValue, setEditingDesigValue] = useState("");
  const [editingRelationshipId, setEditingRelationshipId] = useState(null);
  const [editingRelationshipValue, setEditingRelationshipValue] = useState("");
  const [editingBloodGroupId, setEditingBloodGroupId] = useState(null);
  const [editingBloodGroupValue, setEditingBloodGroupValue] = useState("");
  const [editingTherapeuticClassId, setEditingTherapeuticClassId] = useState(null);
  const [editingTherapeuticClassValue, setEditingTherapeuticClassValue] = useState("");
  const [editingMedicineSaltId, setEditingMedicineSaltId] = useState(null);
  const [editingMedicineSaltData, setEditingMedicineSaltData] = useState({});
  const [editingMedicalPrescriptionId, setEditingMedicalPrescriptionId] = useState(null);
  const [editingMedicalPrescriptionData, setEditingMedicalPrescriptionData] = useState({});
  const [editingMedicineDosageId, setEditingMedicineDosageId] = useState(null);
  const [editingMedicineDosageValue, setEditingMedicineDosageValue] = useState("");
  const [editingPatientPrescriptionMedicineId, setEditingPatientPrescriptionMedicineId] = useState(null);
  const [editingPatientPrescriptionMedicineData, setEditingPatientPrescriptionMedicineData] = useState({});
  const [editingPatientCaseId, setEditingPatientCaseId] = useState(null);
  const [editingPatientCaseData, setEditingPatientCaseData] = useState({});
  const [editingPatientComplaintId, setEditingPatientComplaintId] = useState(null);
  const [editingPatientComplaintData, setEditingPatientComplaintData] = useState({});
  const [editingCaseDiagnosisId, setEditingCaseDiagnosisId] = useState(null);
  const [editingCaseDiagnosisData, setEditingCaseDiagnosisData] = useState({});
  const [editingPatientInvestigationId, setEditingPatientInvestigationId] = useState(null);
  const [editingPatientInvestigationData, setEditingPatientInvestigationData] = useState({});
  const [editingProfileTopicId, setEditingProfileTopicId] = useState(null);
  const [editingProfileTopicValue, setEditingProfileTopicValue] = useState("");
  const [editingProfileSubTopicId, setEditingProfileSubTopicId] = useState(null);
  const [editingProfileSubTopicData, setEditingProfileSubTopicData] = useState({});
  const [editingServiceTypeId, setEditingServiceTypeId] = useState(null);
  const [editingServiceTypeValue, setEditingServiceTypeValue] = useState("");
  const [editingMedicalServiceId, setEditingMedicalServiceId] = useState(null);
  const [editingMedicalServiceValue, setEditingMedicalServiceValue] = useState("");
  const [editingServiceId, setEditingServiceId] = useState(null);
  const [editingServiceData, setEditingServiceData] = useState({});
  const [editingQuestionTypeId, setEditingQuestionTypeId] = useState(null);
  const [editingQuestionTypeValue, setEditingQuestionTypeValue] = useState("");
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [editingQuestionData, setEditingQuestionData] = useState({});
  const [editingOptionId, setEditingOptionId] = useState(null);
  const [editingOptionData, setEditingOptionData] = useState({});
  const [editingQuestionGroupId, setEditingQuestionGroupId] = useState(null);
  const [editingQuestionGroupData, setEditingQuestionGroupData] = useState({});
  const [editingEventTypeId, setEditingEventTypeId] = useState(null);
  const [editingEventTypeValue, setEditingEventTypeValue] = useState("");
  const [editingImmunizationId, setEditingImmunizationId] = useState(null);
  const [editingImmunizationData, setEditingImmunizationData] = useState({});
  const [editingVaccineId, setEditingVaccineId] = useState(null);
  const [editingVaccineData, setEditingVaccineData] = useState({});
  const [editingSymptomCategoryId, setEditingSymptomCategoryId] = useState(null);
  const [editingSymptomCategoryValue, setEditingSymptomCategoryValue] = useState("");
  const [editingSupplierTypeId, setEditingSupplierTypeId] = useState(null);
  const [editingSupplierTypeValue, setEditingSupplierTypeValue] = useState("");
  const [editingProductCategoryId, setEditingProductCategoryId] = useState(null);
  const [editingProductCategoryData, setEditingProductCategoryData] = useState({});
  const [editingProductSubCategoryId, setEditingProductSubCategoryId] = useState(null);
  const [editingProductSubCategoryData, setEditingProductSubCategoryData] = useState({});
  const [editingProductId, setEditingProductId] = useState(null);
  const [editingProductData, setEditingProductData] = useState({ status: "Active" });
  const [editingMvtJourneyPhaseId, setEditingMvtJourneyPhaseId] = useState(null);
  const [editingMvtJourneyPhaseValue, setEditingMvtJourneyPhaseValue] = useState("");
  const [editingMvtJourneyStepId, setEditingMvtJourneyStepId] = useState(null);
  const [editingMvtJourneyStepData, setEditingMvtJourneyStepData] = useState({});

  const fetchEstablishmentTypes = useCallback(() => {
    fetch(`${API_BASE}/master/establishment-types`)
      .then((r) => {
        if (!r.ok) throw new Error(`Server error ${r.status}`);
        const ct = r.headers.get("content-type") || "";
        if (!ct.includes("application/json")) throw new Error("Backend not reachable — is the server running?");
        return r.json();
      })
      .then((data) => {
        if (Array.isArray(data)) setEstTypes(data);
      })
      .catch((e) => console.error("Failed to load establishment types:", e.message));
  }, []);

  const fetchEstablishmentSubTypes = useCallback(() => {
    fetch(`${API_BASE}/master/establishment-sub-types`)
      .then((r) => {
        if (!r.ok) throw new Error(`Server error ${r.status}`);
        const ct = r.headers.get("content-type") || "";
        if (!ct.includes("application/json")) throw new Error("Backend not reachable — is the server running?");
        return r.json();
      })
      .then((data) => {
        if (Array.isArray(data)) setEstablishmentSubTypes(data);
      })
      .catch((e) => console.error("Failed to load establishment sub types:", e.message));
  }, []);

  useEffect(() => {
    const load = (url, setter) =>
      fetch(url)
        .then((r) => {
          if (!r.ok) throw new Error(`Server error ${r.status}`);
          const ct = r.headers.get('content-type') || '';
          if (!ct.includes('application/json')) throw new Error('Backend not reachable — is the server running?');
          return r.json();
        })
        .then(setter)
        .catch((e) => console.error(`Failed to load ${url}:`, e.message));
    load(`${API_BASE}/master/user-types`, setUserTypes);
    load(`${API_BASE}/locations/types`, setLocationTypes);
    load(`${API_BASE}/locations`, setLocations);
    load(`${API_BASE}/master/specialties`, setSpecialties);
    load(`${API_BASE}/master/medical-procedures`, setMedicalProcedures);
    load(`${API_BASE}/master/diseases`, setDiseases);
    load(`${API_BASE}/master/symptom-categories`, setSymptomCategories);
    load(`${API_BASE}/master/symptoms`, setSymptoms);
    load(`${API_BASE}/master/aggravating-factors`, setAggravatingFactors);
    load(`${API_BASE}/master/allergy-types`, setAllergyTypes);
    load(`${API_BASE}/master/allergies`, setAllergies);
    load(`${API_BASE}/master/trauma-types`, setTraumaTypes);
    load(`${API_BASE}/master/traumas`, setTraumas);
    load(`${API_BASE}/master/addiction-categories`, setAddictionCategories);
    load(`${API_BASE}/master/addiction-sub-categories`, setAddictionSubCategories);
    load(`${API_BASE}/master/addictions`, setAddictionsList);
    load(`${API_BASE}/master/patient-conditions`, setPatientConditions);
    load(`${API_BASE}/master/investigation-types`, setInvestigationTypes);
    load(`${API_BASE}/master/investigation-groups`, setInvestigationGroups);
    load(`${API_BASE}/master/investigations`, setInvestigations);
    load(`${API_BASE}/master/establishment-types`, setEstTypes);
    load(`${API_BASE}/master/establishment-sub-types`, setEstablishmentSubTypes);
    load(`${API_BASE}/master/degrees`, setDegrees);
    load(`${API_BASE}/master/specializations`, setSpecializations);
    load(`${API_BASE}/master/specialization-types`, setSpecializationTypes);
    load(`${API_BASE}/master/accreditations`, setAccreditations);
    load(`${API_BASE}/master/department-categories`, setDepartmentCategories);
    load(`${API_BASE}/master/departments`, setDepartments);
    load(`${API_BASE}/master/designations`, setDesignations);
    load(`${API_BASE}/master/relationships`, setRelationships);
    load(`${API_BASE}/master/blood-groups`, setBloodGroups);
    load(`${API_BASE}/master/therapeutic-classes`, setTherapeuticClasses);
    load(`${API_BASE}/master/medicine-salts`, setMedicineSalts);
    load(`${API_BASE}/master/medical-prescriptions`, setMedicalPrescriptions);
    load(`${API_BASE}/master/dosages`, setMedicineDosages);
    load(`${API_BASE}/master/patient-prescription-medicines`, setPatientPrescriptionMedicines);
    load(`${API_BASE}/master/patient-cases`, setPatientCases);
    load(`${API_BASE}/master/patient-complaints`, setPatientComplaints);
    load(`${API_BASE}/master/case-diagnoses`, setCaseDiagnoses);
    load(`${API_BASE}/master/patient-investigations`, setPatientInvestigations);
    load(`${API_BASE}/master/profile-topics`, setProfileTopics);
    load(`${API_BASE}/master/profile-sub-topics`, setProfileSubTopics);
    load(`${API_BASE}/master/service-types`, setServiceTypes);
    load(`${API_BASE}/master/medical-services`, setMedicalServices);
    load(`${API_BASE}/master/services`, setServices);
    load(`${API_BASE}/master/question-types`, setQuestionTypes);
    load(`${API_BASE}/master/questions`, setQuestions);
    load(`${API_BASE}/master/options`, setOptions);
    load(`${API_BASE}/master/question-groups`, setQuestionGroups);
    load(`${API_BASE}/master/establishments`, setEstablishments);
    load(`${API_BASE}/master/doctors`, setDoctors);
    load(`${API_BASE}/master/patients`, setPatients);
    load(`${API_BASE}/master/users`, setUsers);
    load(`${API_BASE}/master/event-types`, setEventTypes);
    load(`${API_BASE}/master/immunizations`, setImmunizations);
    load(`${API_BASE}/master/vaccines`, setVaccines);
    load(`${API_BASE}/login-logs`, setLoginLogs);
    load(`${API_BASE}/master/supplier-types`, setSupplierTypes);
    load(`${API_BASE}/master/product-categories`, setProductCategories);
    load(`${API_BASE}/master/product-sub-categories`, setProductSubCategories);
    load(`${API_BASE}/master/products`, setProducts);
    load(`${API_BASE}/master/mvt-journey-phases`, setMvtJourneyPhases);
    load(`${API_BASE}/master/mvt-journey-steps`, setMvtJourneySteps);
  }, []);

  useEffect(() => {
    if (currentStep === 20 || currentStep === 21) fetchEstablishmentTypes();
    if (currentStep === 21 || currentStep === 37) fetchEstablishmentSubTypes();
    if (currentStep === 37) fetchEstablishmentTypes();
  }, [currentStep, fetchEstablishmentTypes, fetchEstablishmentSubTypes]);

  useEffect(() => {
    if (currentStep === 59 || currentStep === 60 || currentStep === 61) {
      fetch(`${API_BASE}/master/product-categories`)
        .then((r) => r.ok ? r.json() : [])
        .then((data) => { if (Array.isArray(data)) setProductCategories(data); })
        .catch(() => {});
    }
    if (currentStep === 60 || currentStep === 61) {
      fetch(`${API_BASE}/master/product-sub-categories`)
        .then((r) => r.ok ? r.json() : [])
        .then((data) => { if (Array.isArray(data)) setProductSubCategories(data); })
        .catch(() => {});
    }
    if (currentStep === 61) {
      fetch(`${API_BASE}/master/products`)
        .then((r) => r.ok ? r.json() : [])
        .then((data) => { if (Array.isArray(data)) setProducts(data); })
        .catch(() => {});
      fetch(`${API_BASE}/master/medical-specialties`)
        .then((r) => r.ok ? r.json() : [])
        .then((data) => { if (Array.isArray(data)) setSpecialties(data); })
        .catch(() => {});
    }
    if (currentStep === 71) {
      fetch(`${API_BASE}/master/mvt-journey-phases`)
        .then((r) => r.ok ? r.json() : [])
        .then((data) => { if (Array.isArray(data)) setMvtJourneyPhases(data); })
        .catch(() => {});
    }
  }, [currentStep]);

  useEffect(() => {
    if (isLoginAdminStep(currentStep) && !establishmentLoginForm.password && !createLoginDoctor && currentStep !== 64 && currentStep !== 69) {
      if (currentStep === 45) {
        setEstablishmentLoginForm((p) => ({ ...p, password: generateSystemPassword() }));
      } else if (currentStep === 65) {
        setDoctorLoginForm((p) => ({ ...p, password: generateSystemPassword() }));
      }
    }
    // Never auto-generate a password while editing — that would overwrite the stored password on save.
    if (currentStep === 64 && !locationLoginForm.password && !locationLoginForm.login_id) {
      setLocationLoginForm((p) => ({ ...p, password: generateSystemPassword() }));
    }
    if (currentStep === 69 && !userLoginForm.password && !createLoginUser) {
      setUserLoginForm((p) => ({ ...p, password: generateSystemPassword() }));
    }
  }, [currentStep, establishmentLoginForm.password, doctorLoginForm.password, locationLoginForm.password, userLoginForm.password, createLoginDoctor, createLoginUser]);

  const handleLocationTypeChange = (field, value) =>
    setLocationTypeForm((prev) => ({ ...prev, [field]: value }));

  const handleLocationChange = (field, value) => {
    setLocationForm((prev) => {
      const updated = { ...prev, [field]: value };
      // A location type change also changes the valid parent level.
      if (field === "location_type_id") {
        updated.parent_location_id = "";
        updated.country_code = "";
        updated.country_flag_image_path = "";
        updated.country_flag_image = null;
        updated.treatment_cost_currency = "";
        updated.local_currency = "";
      }
      // Auto-fill country code, flag, and currencies from parent location chain
      if (field === "parent_location_id" && value) {
        const inherited = resolveCountryFieldsFromParent(locations, value);
        if (inherited.country_code) updated.country_code = inherited.country_code;
        if (inherited.country_flag_image_path) {
          updated.country_flag_image_path = inherited.country_flag_image_path;
          updated.country_flag_image = null;
        }
        if (inherited.treatment_cost_currency) {
          updated.treatment_cost_currency = inherited.treatment_cost_currency;
        }
        if (inherited.local_currency) {
          updated.local_currency = inherited.local_currency;
        }
      }
      // Clear inherited fields when top-level selected
      if (field === "parent_location_id" && !value) {
        updated.country_code = "";
        updated.country_flag_image_path = "";
        updated.country_flag_image = null;
        updated.treatment_cost_currency = "";
        updated.local_currency = "";
      }
      return updated;
    });
  };

  const applyQuestionFieldChange = (prev, field, value) => {
    const updated = { ...prev, [field]: value };
    if (field === "question_linking") {
      updated.profile_topic_id = "";
      updated.profile_sub_topic_id = "";
      updated.service_id = "";
    }
    if (field === "question_text") {
      updated.description = value;
    }
    return updated;
  };

  const handleQuestionFormChange = (field, value) => {
    setQuestionForm((prev) => applyQuestionFieldChange(prev, field, value));
  };

  const handleEditingQuestionChange = (field, value) => {
    setEditingQuestionData((prev) => applyQuestionFieldChange(prev, field, value));
  };

  const makeDeleteHandler = ({ buildUrl, idField, setList, confirmMessage, label }) => async (id) => {
    if (!window.confirm(confirmMessage)) return;
    setSubmitMsg("");
    try {
      const res = await fetch(buildUrl(id), { method: "DELETE" });
      if (!res.ok) throw new Error(await parseApiError(res));
      setList((prev) => prev.filter((item) => String(item[idField]) !== String(id)));
      setSubmitMsg(`${label} deleted successfully.`);
    } catch (err) {
      setSubmitMsg(`Error: ${err.message}`);
    }
  };

  // ── User Type edit/delete ──
  const handleEditUserType = (ut) => {
    setEditingUserTypeId(ut.user_type_id);
    setEditingUserTypeValue(ut.user_type);
  };
  const handleSaveUserType = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/user-types/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_type: editingUserTypeValue }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      setUserTypes((prev) =>
        prev.map((ut) => (ut.user_type_id === id ? { ...ut, user_type: editingUserTypeValue } : ut))
      );
      setEditingUserTypeId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteUserType = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/user-types/${id}`,
    idField: "user_type_id",
    setList: setUserTypes,
    confirmMessage: "Delete this user type?",
    label: "User type",
  });

  // ── Location Type edit/delete ──
  const handleEditType = (lt) => {
    setEditingTypeId(lt.location_type_id);
    setEditingTypeValue(lt.location_type);
  };
  const handleSaveType = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/locations/types/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ location_type: editingTypeValue }),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      setLocationTypes((prev) =>
        prev.map((lt) => lt.location_type_id === id ? { ...lt, location_type: editingTypeValue } : lt)
      );
      setEditingTypeId(null);
    } catch (err) { setSubmitMsg(`Error: ${err.message}`); }
  };
  const handleDeleteType = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/locations/types/${id}`,
    idField: "location_type_id",
    setList: setLocationTypes,
    confirmMessage: "Delete this location type?",
    label: "Location type",
  });

  // ── Location edit/delete ──
  const handleEditLoc = (loc) => {
    setEditingLocId(loc.location_id);
    setEditingLocData({ ...loc });
  };
  const handleSaveLoc = async (id) => {
    try {
      const fd = new FormData();
      fd.append("location_type_id", editingLocData.location_type_id);
      fd.append("parent_location_id", editingLocData.parent_location_id || "");
      fd.append("location_name", editingLocData.location_name);
      fd.append("location_category", editingLocData.location_category || "");
      fd.append("country_code", editingLocData.country_code || "");
      fd.append("treatment_cost_currency", editingLocData.treatment_cost_currency || "");
      fd.append("local_currency", editingLocData.local_currency || "");
      if (editingLocData.icon_image instanceof File) fd.append("icon_image", editingLocData.icon_image);
      if (editingLocData.country_flag_image instanceof File) {
        fd.append("country_flag_image", editingLocData.country_flag_image);
      } else if (editingLocData.country_flag_image_path) {
        fd.append("country_flag_image_path", editingLocData.country_flag_image_path);
      }
      const res = await fetch(`${API_BASE}/locations/${id}`, { method: "PUT", body: fd });
      if (!res.ok) throw new Error((await res.json()).message);
      const listRes = await fetch(`${API_BASE}/locations`);
      if (listRes.ok) setLocations(await listRes.json());
      setEditingLocId(null);
    } catch (err) { setSubmitMsg(`Error: ${err.message}`); }
  };
  const handleDeleteLoc = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/locations/${id}`,
    idField: "location_id",
    setList: setLocations,
    confirmMessage: "Delete this location?",
    label: "Location",
  });

  // ── Medical Specialty edit/delete ──
  const handleEditSpec = (s) => {
    setEditingSpecId(s.medical_specialty_id);
    setEditingSpecData({ ...s });
  };
  const handleSaveSpec = async (id) => {
    try {
      const fd = new FormData();
      fd.append("medical_specialty", editingSpecData.medical_specialty);
      fd.append("medical_specialty_type", editingSpecData.medical_specialty_type);
      fd.append("mvt_relevance", editingSpecData.mvt_relevance || "No");
      if (editingSpecData.icon_image instanceof File) fd.append("icon_image", editingSpecData.icon_image);
      const res = await fetch(`${API_BASE}/master/specialties/${id}`, { method: "PUT", body: fd });
      if (!res.ok) throw new Error((await res.json()).message);
      const listRes = await fetch(`${API_BASE}/master/specialties`);
      if (listRes.ok) setSpecialties(await listRes.json());
      setEditingSpecId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteSpec = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/specialties/${id}`,
    idField: "medical_specialty_id",
    setList: setSpecialties,
    confirmMessage: "Delete this specialty?",
    label: "Specialty",
  });

  // ── Medical Procedure edit/delete ──
  const handleEditProc = (p) => {
    setEditingProcId(p.procedure_id);
    setEditingProcData({ ...p });
  };
  const handleSaveProc = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/medical-procedures/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          medical_specialty_id: editingProcData.medical_specialty_id,
          procedure_name: editingProcData.procedure_name,
          alternate_name: editingProcData.alternate_name,
          surgical_approach: editingProcData.surgical_approach,
          complexity_level: editingProcData.complexity_level,
          icd10_pcs_code: editingProcData.icd10_pcs_code,
          cpt_reference_code: editingProcData.cpt_reference_code,
          clinical_notes: editingProcData.clinical_notes,
          est_cost_usd_low: editingProcData.est_cost_usd_low,
          est_cost_usd_high: editingProcData.est_cost_usd_high,
          est_cost_inr_low: editingProcData.est_cost_inr_low,
          est_cost_inr_high: editingProcData.est_cost_inr_high,
          past_surgery_relevance: editingProcData.past_surgery_relevance,
          mvt_relevance: editingProcData.mvt_relevance || "No",
        }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      const specialtyName = specialties.find(
        (s) => String(s.medical_specialty_id) === String(editingProcData.medical_specialty_id)
      )?.medical_specialty;
      setMedicalProcedures((prev) =>
        prev.map((p) =>
          p.procedure_id === id
            ? { ...p, ...editingProcData, medical_specialty: specialtyName }
            : p
        )
      );
      setEditingProcId(null);
      setSubmitMsg("Medical Procedure updated successfully!");
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteProc = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/medical-procedures/${id}`,
    idField: "procedure_id",
    setList: setMedicalProcedures,
    confirmMessage: "Delete this medical procedure?",
    label: "Medical procedure",
  });

  // ── Disease edit/delete ──
  const handleEditDisease = (d) => {
    setEditingDiseaseId(d.disease_id);
    setEditingDiseaseData({ ...d });
  };
  const handleSaveDisease = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/diseases/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          medical_specialty_id: editingDiseaseData.medical_specialty_id,
          disease_name: editingDiseaseData.disease_name,
          description: editingDiseaseData.description,
          comorbidity_disease: editingDiseaseData.comorbidity_disease,
          family_history_disease: editingDiseaseData.family_history_disease,
          past_illness_disease: editingDiseaseData.past_illness_disease,
          icd10_code: editingDiseaseData.icd10_code,
          icd11_code: editingDiseaseData.icd11_code,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      const specialtyName = specialties.find(
        (s) => String(s.medical_specialty_id) === String(editingDiseaseData.medical_specialty_id)
      )?.medical_specialty;
      setDiseases((prev) =>
        prev.map((d) =>
          d.disease_id === id
            ? { ...d, ...editingDiseaseData, medical_specialty: specialtyName }
            : d
        )
      );
      setEditingDiseaseId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteDisease = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/diseases/${id}`,
    idField: "disease_id",
    setList: setDiseases,
    confirmMessage: "Delete this disease?",
    label: "Disease",
  });

  // ── Establishment Type edit/delete ──
  const handleEditEst = (e) => {
    setEditingEstId(e.establishment_type_id);
    setEditingEstValue(e.establishment_type);
  };
  const handleSaveEst = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/master/establishment-types/${id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ establishment_type: editingEstValue }),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      setEstTypes((prev) => prev.map((e) => {
        if (String(e.establishment_type_id) !== String(id)) return e;
        return { ...e, establishment_type: editingEstValue };
      }));
      setEditingEstId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteEst = async (id) => {
    if (!window.confirm("Delete this establishment type?")) return;
    setSubmitMsg("");
    try {
      const res = await fetch(`${API_BASE}/master/establishment-types/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await parseApiError(res));
      setEstTypes((prev) => prev.filter((item) => String(item.establishment_type_id) !== String(id)));
      setSubmitMsg("Establishment type deleted successfully.");
    } catch (err) {
      setSubmitMsg(`Error: ${err.message}`);
    }
  };

  // ── Establishment Sub Type edit/delete ──
  const handleEditEstSubType = (row) => {
    setEditingEstSubTypeId(row.establishment_sub_type_id);
    setEditingEstSubTypeData({ ...row });
  };
  const handleSaveEstSubType = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/master/establishment-sub-types/${id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          establishment_type_id: editingEstSubTypeData.establishment_type_id,
          establishment_sub_type: editingEstSubTypeData.establishment_sub_type,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      const typeName = estTypes.find(
        (t) => String(t.establishment_type_id) === String(editingEstSubTypeData.establishment_type_id)
      )?.establishment_type;
      setEstablishmentSubTypes((prev) => prev.map((row) => (
        String(row.establishment_sub_type_id) !== String(id)
          ? row
          : { ...row, ...editingEstSubTypeData, establishment_type: typeName }
      )));
      setEditingEstSubTypeId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteEstSubType = async (id) => {
    if (!window.confirm("Delete this establishment sub type?")) return;
    setSubmitMsg("");
    try {
      const res = await fetch(`${API_BASE}/master/establishment-sub-types/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error(await parseApiError(res));
      setEstablishmentSubTypes((prev) => prev.filter((item) => String(item.establishment_sub_type_id) !== String(id)));
      setSubmitMsg("Establishment sub type deleted successfully.");
    } catch (err) {
      setSubmitMsg(`Error: ${err.message}`);
    }
  };

  // ── Supplier Type edit/delete ──
  const handleEditSupplierType = (row) => {
    setEditingSupplierTypeId(row.supplier_type_id);
    setEditingSupplierTypeValue(row.supplier_type);
  };
  const handleSaveSupplierType = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/master/supplier-types/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ supplier_type: editingSupplierTypeValue }),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      setSupplierTypes((prev) => prev.map((row) =>
        String(row.supplier_type_id) === String(id)
          ? { ...row, supplier_type: editingSupplierTypeValue }
          : row
      ));
      setEditingSupplierTypeId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteSupplierType = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/supplier-types/${id}`,
    idField: "supplier_type_id",
    setList: setSupplierTypes,
    confirmMessage: "Delete this supplier type?",
    label: "Supplier type",
  });

  // ── Product Category edit/delete ──
  const handleEditProductCategory = (row) => {
    setEditingProductCategoryId(row.product_category_id);
    setEditingProductCategoryData({ ...row });
  };
  const handleSaveProductCategory = async (id) => {
    try {
      const payload = {
        category_code: editingProductCategoryData.category_code || null,
        product_category: editingProductCategoryData.product_category,
        category_group: editingProductCategoryData.category_group || null,
        status: editingProductCategoryData.status || "Active",
      };
      const res = await fetch(`${API_BASE}/master/product-categories/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      setProductCategories((prev) => prev.map((row) =>
        String(row.product_category_id) === String(id)
          ? { ...row, ...payload }
          : row
      ));
      setEditingProductCategoryId(null);
      setEditingProductCategoryData({});
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteProductCategory = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/product-categories/${id}`,
    idField: "product_category_id",
    setList: setProductCategories,
    confirmMessage: "Delete this product category?",
    label: "Product category",
  });

  // ── Product Sub Category edit/delete ──
  const handleEditProductSubCategory = (row) => {
    setEditingProductSubCategoryId(row.product_sub_category_id);
    setEditingProductSubCategoryData({ ...row });
  };
  const handleSaveProductSubCategory = async (id) => {
    try {
      const payload = {
        product_category_id: editingProductSubCategoryData.product_category_id,
        sub_category_code: editingProductSubCategoryData.sub_category_code || null,
        product_sub_category: editingProductSubCategoryData.product_sub_category,
        sub_category_description: editingProductSubCategoryData.sub_category_description || null,
        hsn_code: editingProductSubCategoryData.hsn_code || null,
        regulatory_body: editingProductSubCategoryData.regulatory_body || null,
        status: editingProductSubCategoryData.status || "Active",
      };
      const res = await fetch(`${API_BASE}/master/product-sub-categories/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      const matchedCategory = productCategories.find(
        (pc) => String(pc.product_category_id) === String(payload.product_category_id)
      );
      setProductSubCategories((prev) => prev.map((row) =>
        String(row.product_sub_category_id) === String(id)
          ? {
              ...row,
              ...payload,
              product_category: matchedCategory?.product_category,
              category_code: matchedCategory?.category_code,
              category_group: matchedCategory?.category_group,
            }
          : row
      ));
      setEditingProductSubCategoryId(null);
      setEditingProductSubCategoryData({});
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteProductSubCategory = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/product-sub-categories/${id}`,
    idField: "product_sub_category_id",
    setList: setProductSubCategories,
    confirmMessage: "Delete this product sub category?",
    label: "Product sub category",
  });

  // ── MVT Journey Phase edit/delete ──
  const handleEditMvtJourneyPhase = (row) => {
    setEditingMvtJourneyPhaseId(row.mvt_journey_phase_id);
    setEditingMvtJourneyPhaseValue(row.mvt_journey_phase);
  };
  const handleSaveMvtJourneyPhase = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/master/mvt-journey-phases/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mvt_journey_phase: editingMvtJourneyPhaseValue }),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      setMvtJourneyPhases((prev) => prev.map((row) =>
        String(row.mvt_journey_phase_id) === String(id)
          ? { ...row, mvt_journey_phase: editingMvtJourneyPhaseValue }
          : row
      ));
      setEditingMvtJourneyPhaseId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteMvtJourneyPhase = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/mvt-journey-phases/${id}`,
    idField: "mvt_journey_phase_id",
    setList: setMvtJourneyPhases,
    confirmMessage: "Delete this MVT journey phase?",
    label: "MVT journey phase",
  });

  // ── MVT Journey Step edit/delete ──
  const handleEditMvtJourneyStep = (row) => {
    setEditingMvtJourneyStepId(row.mvt_journey_step_id);
    setEditingMvtJourneyStepData({ ...row });
  };
  const handleSaveMvtJourneyStep = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/master/mvt-journey-steps/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mvt_journey_phase_id: editingMvtJourneyStepData.mvt_journey_phase_id,
          mvt_journey_step_name: editingMvtJourneyStepData.mvt_journey_step_name,
          tat: editingMvtJourneyStepData.tat,
          input_type: editingMvtJourneyStepData.input_type,
          output_type: editingMvtJourneyStepData.output_type,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      const phaseName = mvtJourneyPhases.find(
        (p) => String(p.mvt_journey_phase_id) === String(editingMvtJourneyStepData.mvt_journey_phase_id)
      )?.mvt_journey_phase;
      setMvtJourneySteps((prev) => prev.map((row) =>
        String(row.mvt_journey_step_id) === String(id)
          ? { ...row, ...editingMvtJourneyStepData, mvt_journey_phase: phaseName }
          : row
      ));
      setEditingMvtJourneyStepId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteMvtJourneyStep = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/mvt-journey-steps/${id}`,
    idField: "mvt_journey_step_id",
    setList: setMvtJourneySteps,
    confirmMessage: "Delete this MVT journey step?",
    label: "MVT journey step",
  });

  // ── Product edit/delete ──
  const handleEditProduct = (row) => {
    setEditingProductId(row.product_id);
    setEditingProductData({
      ...row,
      medical_specialty_ids: row.medical_specialty_ids || "",
      estimated_price_inr: row.estimated_price_inr ?? "",
      estimated_price_usd: row.estimated_price_usd ?? "",
      product_image_files: [],
      product_profile: null,
      removed_product_image_ids: [],
    });
  };
  const handleSaveProduct = async (id) => {
    try {
      const fd = new FormData();
      appendProductFormData(fd, editingProductData);
      const res = await fetch(`${API_BASE}/master/products/${id}`, { method: "PUT", body: fd });
      if (!res.ok) throw new Error((await res.json()).message);
      const listRes = await fetch(`${API_BASE}/master/products`);
      if (listRes.ok) setProducts(await listRes.json());
      setEditingProductId(null);
      setSubmitMsg("Product updated successfully.");
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteProduct = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/products/${id}`,
    idField: "product_id",
    setList: setProducts,
    confirmMessage: "Delete this product?",
    label: "Product",
  });

  const handleProductFormChange = (field, value) => {
    if (field === "product_category_id") {
      setProductForm((p) => ({ ...p, product_category_id: value, product_sub_category_id: "" }));
      return;
    }
    setProductForm((p) => ({ ...p, [field]: value }));
  };

  // ── Medical Degree edit/delete ──
  const handleEditDeg = (d) => { setEditingDegId(d.medical_degree_id); setEditingDegValue(d.medical_degree_title); };
  const handleSaveDeg = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/degrees/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ medical_degree_title: editingDegValue }) });
      if (!r.ok) throw new Error((await r.json()).message);
      setDegrees((prev) => prev.map((d) => d.medical_degree_id === id ? { ...d, medical_degree_title: editingDegValue } : d));
      setEditingDegId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteDeg = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/degrees/${id}`,
    idField: "medical_degree_id",
    setList: setDegrees,
    confirmMessage: "Delete this degree?",
    label: "Degree",
  });

  // ── Specialization edit/delete ──
  const handleEditSpecz = (s) => {
    setEditingSpeczId(s.specialization_id);
    setEditingSpeczData({ ...s });
  };
  const handleSaveSpecz = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/specializations/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          specialization_type_id: editingSpeczData.specialization_type_id,
          specialization: editingSpeczData.specialization,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      const typeName = specializationTypes.find(
        (st) => String(st.specialization_type_id) === String(editingSpeczData.specialization_type_id)
      )?.specialization_type;
      setSpecializations((prev) =>
        prev.map((s) =>
          s.specialization_id === id
            ? { ...s, ...editingSpeczData, specialization_type: typeName }
            : s
        )
      );
      setEditingSpeczId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteSpecz = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/specializations/${id}`,
    idField: "specialization_id",
    setList: setSpecializations,
    confirmMessage: "Delete this specialization?",
    label: "Specialization",
  });

  // ── Specialization Type edit/delete ──
  const handleEditSpecType = (st) => {
    setEditingSpecTypeId(st.specialization_type_id);
    setEditingSpecTypeValue(st.specialization_type);
  };
  const handleSaveSpecType = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/specialization-types/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ specialization_type: editingSpecTypeValue }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      setSpecializationTypes((prev) =>
        prev.map((st) =>
          st.specialization_type_id === id
            ? { ...st, specialization_type: editingSpecTypeValue }
            : st
        )
      );
      setEditingSpecTypeId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteSpecType = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/specialization-types/${id}`,
    idField: "specialization_type_id",
    setList: setSpecializationTypes,
    confirmMessage: "Delete this specialization type?",
    label: "Specialization type",
  });

  // ── Accreditation edit/delete ──
  const handleEditAccr = (a) => { setEditingAccrId(a.accreditation_id); setEditingAccrValue(a.accreditation_title); };
  const handleSaveAccr = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/accreditations/${id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ accreditation_title: editingAccrValue }) });
      if (!r.ok) throw new Error((await r.json()).message);
      setAccreditations((prev) => prev.map((a) => a.accreditation_id === id ? { ...a, accreditation_title: editingAccrValue } : a));
      setEditingAccrId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteAccr = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/accreditations/${id}`,
    idField: "accreditation_id",
    setList: setAccreditations,
    confirmMessage: "Delete this accreditation?",
    label: "Accreditation",
  });

  // ── Department Category edit/delete ──
  const handleEditDeptCat = (dc) => {
    setEditingDeptCatId(dc.department_category_id);
    setEditingDeptCatValue(dc.department_category);
  };
  const handleSaveDeptCat = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/department-categories/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ department_category: editingDeptCatValue }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      setDepartmentCategories((prev) =>
        prev.map((dc) =>
          dc.department_category_id === id
            ? { ...dc, department_category: editingDeptCatValue }
            : dc
        )
      );
      setEditingDeptCatId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteDeptCat = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/department-categories/${id}`,
    idField: "department_category_id",
    setList: setDepartmentCategories,
    confirmMessage: "Delete this department type?",
    label: "Department type",
  });

  // ── Department edit/delete ──
  const handleEditDept = (d) => {
    setEditingDeptId(d.department_id);
    setEditingDeptData({ ...d });
  };
  const handleSaveDept = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/departments/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          department_category_id: editingDeptData.department_category_id,
          department_name: editingDeptData.department_name,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      const catName = departmentCategories.find(
        (dc) => String(dc.department_category_id) === String(editingDeptData.department_category_id)
      )?.department_category;
      setDepartments((prev) =>
        prev.map((d) =>
          d.department_id === id
            ? { ...d, ...editingDeptData, department_category: catName }
            : d
        )
      );
      setEditingDeptId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteDept = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/departments/${id}`,
    idField: "department_id",
    setList: setDepartments,
    confirmMessage: "Delete this hospital department?",
    label: "Hospital department",
  });

  // ── Designation edit/delete ──
  const handleEditDesig = (d) => {
    setEditingDesigId(d.designation_id);
    setEditingDesigValue(d.designation);
  };
  const handleSaveDesig = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/designations/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ designation: editingDesigValue }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      setDesignations((prev) =>
        prev.map((d) => (d.designation_id === id ? { ...d, designation: editingDesigValue } : d))
      );
      setEditingDesigId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteDesig = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/designations/${id}`,
    idField: "designation_id",
    setList: setDesignations,
    confirmMessage: "Delete this designation?",
    label: "Designation",
  });

  // ── Relationship edit/delete ──
  const handleEditRelationship = (r) => {
    setEditingRelationshipId(r.relationship_id);
    setEditingRelationshipValue(r.relationship);
  };
  const handleSaveRelationship = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/master/relationships/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ relationship: editingRelationshipValue }),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      setRelationships((prev) =>
        prev.map((r) => (r.relationship_id === id ? { ...r, relationship: editingRelationshipValue } : r))
      );
      setEditingRelationshipId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteRelationship = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/relationships/${id}`,
    idField: "relationship_id",
    setList: setRelationships,
    confirmMessage: "Delete this relationship?",
    label: "Relationship",
  });

  // ── Blood Group edit/delete ──
  const handleEditBloodGroup = (bg) => {
    setEditingBloodGroupId(bg.blood_group_id);
    setEditingBloodGroupValue(bg.blood_group);
  };
  const handleSaveBloodGroup = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/master/blood-groups/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ blood_group: editingBloodGroupValue }),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      setBloodGroups((prev) =>
        prev.map((bg) => (bg.blood_group_id === id ? { ...bg, blood_group: editingBloodGroupValue } : bg))
      );
      setEditingBloodGroupId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteBloodGroup = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/blood-groups/${id}`,
    idField: "blood_group_id",
    setList: setBloodGroups,
    confirmMessage: "Delete this blood group?",
    label: "Blood group",
  });

  // ── Therapeutic Class edit/delete ──
  const handleEditTherapeuticClass = (tc) => {
    setEditingTherapeuticClassId(tc.therapeutic_class_id);
    setEditingTherapeuticClassValue(tc.therapeutic_class);
  };
  const handleSaveTherapeuticClass = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/master/therapeutic-classes/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ therapeutic_class: editingTherapeuticClassValue }),
      });
      if (!res.ok) throw new Error((await res.json()).message);
      setTherapeuticClasses((prev) =>
        prev.map((tc) =>
          tc.therapeutic_class_id === id
            ? { ...tc, therapeutic_class: editingTherapeuticClassValue }
            : tc
        )
      );
      setEditingTherapeuticClassId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteTherapeuticClass = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/therapeutic-classes/${id}`,
    idField: "therapeutic_class_id",
    setList: setTherapeuticClasses,
    confirmMessage: "Delete this therapeutic class?",
    label: "Therapeutic class",
  });

  // ── Medicine Salt edit/delete ──
  const handleEditMedicineSalt = (ms) => {
    setEditingMedicineSaltId(ms.salt_id);
    setEditingMedicineSaltData({ ...ms });
  };
  const handleSaveMedicineSalt = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/medicine-salts/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          therapeutic_class_id: editingMedicineSaltData.therapeutic_class_id,
          salt_name: editingMedicineSaltData.salt_name,
          chemical_formula: editingMedicineSaltData.chemical_formula,
          description: editingMedicineSaltData.description,
          salt_schedule: editingMedicineSaltData.salt_schedule,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      const listRes = await fetch(`${API_BASE}/master/medicine-salts`);
      if (listRes.ok) setMedicineSalts(await listRes.json());
      setEditingMedicineSaltId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteMedicineSalt = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/medicine-salts/${id}`,
    idField: "salt_id",
    setList: setMedicineSalts,
    confirmMessage: "Delete this medicine salt?",
    label: "Medicine salt",
  });

  const handleMedicalPrescriptionChange = (field, value) => {
    setMedicalPrescriptionForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "patient_id") {
        next.case_id = "";
        next.parent_case_id = "";
      }
      if (field === "case_id" && value) {
        const selectedCase = patientCases.find((c) => String(c.case_id) === String(value));
        if (selectedCase) {
          next.patient_id = String(selectedCase.patient_id);
          if (String(next.parent_case_id) === String(value)) next.parent_case_id = "";
        }
      }
      return next;
    });
  };

  const handleEditMedicalPrescription = (rx) => {
    setEditingMedicalPrescriptionId(rx.prescription_id);
    setEditingMedicalPrescriptionData({
      ...rx,
      prescription_date: rx.prescription_date
        ? String(rx.prescription_date).slice(0, 10)
        : "",
    });
  };

  const handleSaveMedicalPrescription = async (id) => {
    try {
      const fd = new FormData();
      appendPrescriptionFormData(fd, editingMedicalPrescriptionData, true);
      const r = await fetch(`${API_BASE}/master/medical-prescriptions/${id}`, { method: "PUT", body: fd });
      if (!r.ok) throw new Error((await r.json()).message);
      const listRes = await fetch(`${API_BASE}/master/medical-prescriptions`);
      if (listRes.ok) setMedicalPrescriptions(await listRes.json());
      setEditingMedicalPrescriptionId(null);
      setSubmitMsg("Medical prescription updated successfully!");
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };

  const handleDeleteMedicalPrescription = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/medical-prescriptions/${id}`,
    idField: "prescription_id",
    setList: setMedicalPrescriptions,
    confirmMessage: "Delete this medical prescription?",
    label: "Medical prescription",
  });

  const handleEditMedicineDosage = (row) => {
    setEditingMedicineDosageId(row.dosage_id);
    setEditingMedicineDosageValue(row.dosage_type || "");
  };

  const handleSaveMedicineDosage = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/dosages/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ dosage_type: editingMedicineDosageValue }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      const listRes = await fetch(`${API_BASE}/master/dosages`);
      if (listRes.ok) setMedicineDosages(await listRes.json());
      setEditingMedicineDosageId(null);
      setSubmitMsg("Dosage type updated successfully!");
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };

  const handleDeleteMedicineDosage = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/dosages/${id}`,
    idField: "dosage_id",
    setList: setMedicineDosages,
    confirmMessage: "Delete this dosage type?",
    label: "Dosage type",
  });

  const handlePatientPrescriptionMedicineChange = (field, value) => {
    setPatientPrescriptionMedicineForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "patient_id") {
        next.case_id = "";
        next.prescription_id = "";
      }
      if (field === "case_id") {
        next.prescription_id = "";
        if (value) {
          const selectedCase = patientCases.find((c) => String(c.case_id) === String(value));
          if (selectedCase) next.patient_id = String(selectedCase.patient_id);
        }
      }
      return next;
    });
  };

  const handleEditPatientPrescriptionMedicine = (row) => {
    setEditingPatientPrescriptionMedicineId(row.patient_prescription_medicine_id);
    setEditingPatientPrescriptionMedicineData({
      ...row,
      is_empty_stomach: row.is_empty_stomach ? 1 : 0,
      is_sos: row.is_sos ? 1 : 0,
    });
  };

  const buildPatientPrescriptionMedicinePayload = (data) => ({
    case_id: data.case_id,
    patient_id: data.patient_id,
    prescription_id: data.prescription_id,
    salt_id: data.salt_id,
    dosage_id: data.dosage_id,
    duration_days: data.duration_days === "" || data.duration_days == null ? null : Number(data.duration_days),
    is_empty_stomach: Number(data.is_empty_stomach) === 1,
    is_sos: Number(data.is_sos) === 1,
  });

  const handleSavePatientPrescriptionMedicine = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/patient-prescription-medicines/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPatientPrescriptionMedicinePayload(editingPatientPrescriptionMedicineData)),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      const listRes = await fetch(`${API_BASE}/master/patient-prescription-medicines`);
      if (listRes.ok) setPatientPrescriptionMedicines(await listRes.json());
      setEditingPatientPrescriptionMedicineId(null);
      setSubmitMsg("Patient prescription medicine updated successfully!");
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };

  const handleDeletePatientPrescriptionMedicine = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/patient-prescription-medicines/${id}`,
    idField: "patient_prescription_medicine_id",
    setList: setPatientPrescriptionMedicines,
    confirmMessage: "Delete this patient prescription medicine?",
    label: "Patient prescription medicine",
  });

  const toDatetimeLocalValue = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const handlePatientCaseChange = (field, value) => {
    setPatientCaseForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditPatientCase = (row) => {
    setEditingPatientCaseId(row.case_id);
    setEditingPatientCaseData({
      ...row,
      patient_id: row.patient_id,
      referring_doctor_id: row.referring_doctor_id ?? "",
      medical_specialty_id: row.medical_specialty_id ?? "",
      medical_service_id: row.medical_service_id ?? "",
      case_date_time: toDatetimeLocalValue(row.case_date_time),
      comments: row.comments ?? "",
    });
  };

  const buildPatientCasePayload = (data) => ({
    patient_id: Number(data.patient_id),
    referring_doctor_id: data.referring_doctor_id ? Number(data.referring_doctor_id) : null,
    medical_specialty_id: data.medical_specialty_id ? Number(data.medical_specialty_id) : null,
    medical_service_id: data.medical_service_id ? Number(data.medical_service_id) : null,
    case_date_time: data.case_date_time ? new Date(data.case_date_time).toISOString() : null,
    comments: data.comments?.trim() || null,
  });

  const handleSavePatientCase = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/patient-cases/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPatientCasePayload(editingPatientCaseData)),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      const listRes = await fetch(`${API_BASE}/master/patient-cases`);
      if (listRes.ok) setPatientCases(await listRes.json());
      setEditingPatientCaseId(null);
      setSubmitMsg("Patient case updated successfully!");
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };

  const handleDeletePatientCase = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/patient-cases/${id}`,
    idField: "case_id",
    setList: setPatientCases,
    confirmMessage: "Delete this patient case?",
    label: "Patient case",
  });

  const handlePatientComplaintChange = (field, value) => {
    setPatientComplaintForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "patient_id") next.case_id = "";
      if (field === "aggravating_factor_ids") {
        next.aggravating_factors = (value || []).map((id) => {
          const row = aggravatingFactors.find(
            (af) => String(af.aggravating_factor_id) === String(id)
          );
          return row?.aggravating_factor || "";
        }).filter(Boolean);
      }
      return next;
    });
  };

  const buildPatientComplaintPayload = (data) => ({
    symptom_id: data.symptom_id ? Number(data.symptom_id) : null,
    complaint: data.complaint,
    severity: data.severity === "" || data.severity == null ? null : Number(data.severity),
    aggravating_factor_ids: (data.aggravating_factor_ids || []).map(Number),
    aggravating_factors: data.aggravating_factors || [],
    is_sos: Boolean(data.is_sos),
  });

  const handleEditPatientComplaint = (row) => {
    setEditingPatientComplaintId(row.patient_complaint_id);
    setEditingPatientComplaintData({
      ...row,
      patient_id: row.patient_id,
      case_id: row.case_id,
      symptom_id: row.symptom_id ?? "",
      complaint: row.complaint ?? "",
      severity: row.severity ?? "",
      aggravating_factor_ids: (row.aggravating_factor_ids || []).map(String),
      aggravating_factors: row.aggravating_factors || [],
      is_sos: Boolean(row.is_sos),
    });
  };

  const handleSavePatientComplaint = async (row) => {
    try {
      const r = await fetch(
        `${API_BASE}/master/patient-cases/${row.case_id}/complaints/${row.patient_complaint_id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(buildPatientComplaintPayload(editingPatientComplaintData)),
        }
      );
      if (!r.ok) throw new Error((await r.json()).message);
      const listRes = await fetch(`${API_BASE}/master/patient-complaints`);
      if (listRes.ok) setPatientComplaints(await listRes.json());
      setEditingPatientComplaintId(null);
      setSubmitMsg("Patient complaint updated successfully!");
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };

  const handleDeletePatientComplaint = async (row) => {
    if (!window.confirm("Delete this patient complaint?")) return;
    setSubmitMsg("");
    try {
      const res = await fetch(
        `${API_BASE}/master/patient-cases/${row.case_id}/complaints/${row.patient_complaint_id}`,
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error(await parseApiError(res));
      setPatientComplaints((prev) =>
        prev.filter((item) => item.patient_complaint_id !== row.patient_complaint_id)
      );
      setSubmitMsg("Patient complaint deleted successfully.");
    } catch (err) {
      setSubmitMsg(`Error: ${err.message}`);
    }
  };

  const handleCaseDiagnosisChange = (field, value) => {
    setCaseDiagnosisForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "patient_id") next.case_id = "";
      return next;
    });
  };

  const buildCaseDiagnosisPayload = (data) => ({
    disease_id: data.disease_id ? Number(data.disease_id) : null,
    diagnosis: data.diagnosis,
    status: data.status || "Provisional",
    comments: data.comments?.trim() || null,
  });

  const handleEditCaseDiagnosis = (row) => {
    setEditingCaseDiagnosisId(row.case_diagnosis_id);
    setEditingCaseDiagnosisData({
      ...row,
      patient_id: row.patient_id,
      case_id: row.case_id,
      disease_id: row.disease_id ?? "",
      diagnosis: row.diagnosis ?? "",
      status: row.status || "Provisional",
      comments: row.comments ?? "",
    });
  };

  const handleSaveCaseDiagnosis = async (row) => {
    try {
      const r = await fetch(
        `${API_BASE}/master/patient-cases/${row.case_id}/diagnoses/${row.case_diagnosis_id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(buildCaseDiagnosisPayload(editingCaseDiagnosisData)),
        }
      );
      if (!r.ok) throw new Error((await r.json()).message);
      const listRes = await fetch(`${API_BASE}/master/case-diagnoses`);
      if (listRes.ok) setCaseDiagnoses(await listRes.json());
      setEditingCaseDiagnosisId(null);
      setSubmitMsg("Case diagnosis updated successfully!");
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };

  const handleDeleteCaseDiagnosis = async (row) => {
    if (!window.confirm("Delete this case diagnosis?")) return;
    setSubmitMsg("");
    try {
      const res = await fetch(
        `${API_BASE}/master/patient-cases/${row.case_id}/diagnoses/${row.case_diagnosis_id}`,
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error(await parseApiError(res));
      setCaseDiagnoses((prev) =>
        prev.filter((item) => item.case_diagnosis_id !== row.case_diagnosis_id)
      );
      setSubmitMsg("Case diagnosis deleted successfully.");
    } catch (err) {
      setSubmitMsg(`Error: ${err.message}`);
    }
  };

  const handlePatientInvestigationChange = (field, value) => {
    setPatientInvestigationForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "patient_id") {
        next.case_id = "";
        next.case_diagnosis_id = "";
      }
      if (field === "case_id") next.case_diagnosis_id = "";
      if (field === "abnormalities_text") {
        next.abnormalities = value
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean);
      }
      return next;
    });
  };

  const buildPatientInvestigationPayload = (data) => ({
    case_diagnosis_id: data.case_diagnosis_id ? Number(data.case_diagnosis_id) : null,
    investigation_id: data.investigation_id ? Number(data.investigation_id) : null,
    abnormality_ids: data.abnormality_ids || [],
    abnormalities: data.abnormalities
      || (data.abnormalities_text
        ? data.abnormalities_text.split(",").map((item) => item.trim()).filter(Boolean)
        : []),
    investigation_date: data.investigation_date || null,
    comments: data.comments?.trim() || null,
  });

  const handleEditPatientInvestigation = (row) => {
    setEditingPatientInvestigationId(row.patient_investigation_id);
    setEditingPatientInvestigationData({
      ...row,
      patient_id: row.patient_id,
      case_id: row.case_id,
      case_diagnosis_id: row.case_diagnosis_id ?? "",
      investigation_id: row.investigation_id ?? "",
      upload_scan_path: row.upload_scan_path ?? "",
      upload_report_path: row.upload_report_path ?? "",
      abnormalities: row.abnormalities || [],
      abnormalities_text: (row.abnormalities || []).join(", "),
      investigation_date: row.investigation_date ? String(row.investigation_date).slice(0, 10) : "",
      comments: row.comments ?? "",
    });
  };

  const handleSavePatientInvestigation = async (row) => {
    try {
      const fd = new FormData();
      appendInvestigationFormData(fd, editingPatientInvestigationData, true);
      const r = await fetch(
        `${API_BASE}/master/patient-cases/${row.case_id}/investigations/${row.patient_investigation_id}`,
        { method: "PUT", body: fd }
      );
      if (!r.ok) throw new Error((await r.json()).message);
      const listRes = await fetch(`${API_BASE}/master/patient-investigations`);
      if (listRes.ok) setPatientInvestigations(await listRes.json());
      setEditingPatientInvestigationId(null);
      setSubmitMsg("Patient investigation updated successfully!");
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };

  const handleDeletePatientInvestigation = async (row) => {
    if (!window.confirm("Delete this patient investigation?")) return;
    setSubmitMsg("");
    try {
      const res = await fetch(
        `${API_BASE}/master/patient-cases/${row.case_id}/investigations/${row.patient_investigation_id}`,
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error(await parseApiError(res));
      setPatientInvestigations((prev) =>
        prev.filter((item) => item.patient_investigation_id !== row.patient_investigation_id)
      );
      setSubmitMsg("Patient investigation deleted successfully.");
    } catch (err) {
      setSubmitMsg(`Error: ${err.message}`);
    }
  };

  // ── Profile Topic edit/delete ──
  const handleEditProfileTopic = (pt) => {
    setEditingProfileTopicId(pt.profile_topic_id);
    setEditingProfileTopicValue(pt.profile_topic);
  };
  const handleSaveProfileTopic = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/profile-topics/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ profile_topic: editingProfileTopicValue }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      setProfileTopics((prev) =>
        prev.map((pt) =>
          pt.profile_topic_id === id ? { ...pt, profile_topic: editingProfileTopicValue } : pt
        )
      );
      setEditingProfileTopicId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteProfileTopic = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/profile-topics/${id}`,
    idField: "profile_topic_id",
    setList: setProfileTopics,
    confirmMessage: "Delete this profile topic?",
    label: "Profile topic",
  });

  // ── Profile Sub Topic edit/delete ──
  const handleEditProfileSubTopic = (st) => {
    setEditingProfileSubTopicId(st.profile_sub_topic_id);
    setEditingProfileSubTopicData({ ...st });
  };
  const handleSaveProfileSubTopic = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/profile-sub-topics/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          profile_topic_id: editingProfileSubTopicData.profile_topic_id,
          profile_sub_topic: editingProfileSubTopicData.profile_sub_topic,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      const topicName = profileTopics.find(
        (pt) => String(pt.profile_topic_id) === String(editingProfileSubTopicData.profile_topic_id)
      )?.profile_topic;
      setProfileSubTopics((prev) =>
        prev.map((st) =>
          st.profile_sub_topic_id === id
            ? { ...st, ...editingProfileSubTopicData, profile_topic: topicName }
            : st
        )
      );
      setEditingProfileSubTopicId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteProfileSubTopic = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/profile-sub-topics/${id}`,
    idField: "profile_sub_topic_id",
    setList: setProfileSubTopics,
    confirmMessage: "Delete this profile sub topic?",
    label: "Profile sub topic",
  });

  // ── Service Type edit/delete ──
  const handleEditServiceType = (st) => {
    setEditingServiceTypeId(st.service_type_id);
    setEditingServiceTypeValue(st.service_type);
  };
  const handleSaveServiceType = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/service-types/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ service_type: editingServiceTypeValue }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      setServiceTypes((prev) =>
        prev.map((st) =>
          st.service_type_id === id ? { ...st, service_type: editingServiceTypeValue } : st
        )
      );
      setEditingServiceTypeId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteServiceType = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/service-types/${id}`,
    idField: "service_type_id",
    setList: setServiceTypes,
    confirmMessage: "Delete this service type?",
    label: "Service type",
  });

  const handleEditMedicalService = (ms) => {
    setEditingMedicalServiceId(ms.medical_service_id);
    setEditingMedicalServiceValue(ms.medical_service);
  };
  const handleSaveMedicalService = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/medical-services/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ medical_service: editingMedicalServiceValue }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      setMedicalServices((prev) =>
        prev.map((ms) =>
          ms.medical_service_id === id ? { ...ms, medical_service: editingMedicalServiceValue } : ms
        )
      );
      setEditingMedicalServiceId(null);
      setSubmitMsg("Medical service updated successfully!");
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteMedicalService = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/medical-services/${id}`,
    idField: "medical_service_id",
    setList: setMedicalServices,
    confirmMessage: "Delete this medical service?",
    label: "Medical service",
  });

  // ── Service edit/delete ──
  const handleEditService = (s) => {
    setEditingServiceId(s.service_id);
    setEditingServiceData({ ...s });
  };
  const handleSaveService = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/services/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          service_type_id: editingServiceData.service_type_id,
          service_title: editingServiceData.service_title,
          description: editingServiceData.description,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      const typeName = serviceTypes.find(
        (st) => String(st.service_type_id) === String(editingServiceData.service_type_id)
      )?.service_type;
      setServices((prev) =>
        prev.map((s) =>
          s.service_id === id
            ? { ...s, ...editingServiceData, service_type: typeName }
            : s
        )
      );
      setEditingServiceId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteService = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/services/${id}`,
    idField: "service_id",
    setList: setServices,
    confirmMessage: "Delete this service?",
    label: "Service",
  });

  // ── Question Type edit/delete ──
  const handleEditQuestionType = (qt) => {
    setEditingQuestionTypeId(qt.question_type_id);
    setEditingQuestionTypeValue(qt.question_type);
  };
  const handleSaveQuestionType = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/question-types/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question_type: editingQuestionTypeValue }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      setQuestionTypes((prev) =>
        prev.map((qt) =>
          qt.question_type_id === id ? { ...qt, question_type: editingQuestionTypeValue } : qt
        )
      );
      setEditingQuestionTypeId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteQuestionType = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/question-types/${id}`,
    idField: "question_type_id",
    setList: setQuestionTypes,
    confirmMessage: "Delete this question type?",
    label: "Question type",
  });

  // ── Question edit/delete ──
  const handleEditQuestion = (q) => {
    const questionLinking = q.question_linking
      || (q.profile_topic_id ? "profile_topic" : null)
      || (q.profile_sub_topic_id ? "profile_sub_topic" : null)
      || (q.service_id ? "service" : null)
      || "";
    setEditingQuestionId(q.question_id);
    setEditingQuestionData({ ...q, question_linking: questionLinking });
  };
  const handleSaveQuestion = async (id) => {
    try {
      const fd = new FormData();
      appendQuestionFormData(fd, editingQuestionData, true);
      const r = await fetch(`${API_BASE}/master/questions/${id}`, { method: "PUT", body: fd });
      if (!r.ok) throw new Error((await r.json()).message);
      const listRes = await fetch(`${API_BASE}/master/questions`);
      if (listRes.ok) setQuestions(await listRes.json());
      setEditingQuestionId(null);
      setSubmitMsg("Question updated successfully!");
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteQuestion = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/questions/${id}`,
    idField: "question_id",
    setList: setQuestions,
    confirmMessage: "Delete this question?",
    label: "Question",
  });

  // ── Option edit/delete ──
  const handleEditOption = (o) => {
    setEditingOptionId(o.option_id);
    setEditingOptionData({ ...o });
  };
  const handleSaveOption = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/options/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question_id: editingOptionData.question_id,
          sequence: editingOptionData.sequence,
          option_text: editingOptionData.option_text,
          weightage: editingOptionData.weightage,
          table_reference: editingOptionData.table_reference || null,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      const questionText = questions.find(
        (q) => String(q.question_id) === String(editingOptionData.question_id)
      )?.question_text;
      setOptions((prev) =>
        prev.map((o) =>
          o.option_id === id
            ? { ...o, ...editingOptionData, question_text: questionText }
            : o
        )
      );
      setEditingOptionId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteOption = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/options/${id}`,
    idField: "option_id",
    setList: setOptions,
    confirmMessage: "Delete this option?",
    label: "Option",
  });

  // ── Question Group edit/delete ──
  const handleEditQuestionGroup = (g) => {
    setEditingQuestionGroupId(g.question_group_id);
    setEditingQuestionGroupData({ ...g });
  };
  const handleSaveQuestionGroup = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/question-groups/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          profile_sub_topic_id: editingQuestionGroupData.profile_sub_topic_id,
          service_id: editingQuestionGroupData.service_id,
          question_group: editingQuestionGroupData.question_group,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      const subTopicName = profileSubTopics.find(
        (pst) => String(pst.profile_sub_topic_id) === String(editingQuestionGroupData.profile_sub_topic_id)
      )?.profile_sub_topic;
      const serviceTitle = services.find(
        (s) => String(s.service_id) === String(editingQuestionGroupData.service_id)
      )?.service_title;
      setQuestionGroups((prev) =>
        prev.map((g) =>
          g.question_group_id === id
            ? { ...g, ...editingQuestionGroupData, profile_sub_topic: subTopicName, service_title: serviceTitle }
            : g
        )
      );
      setEditingQuestionGroupId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteQuestionGroup = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/question-groups/${id}`,
    idField: "question_group_id",
    setList: setQuestionGroups,
    confirmMessage: "Delete this question group?",
    label: "Question group",
  });

  // ── Aggravating Factor edit/delete ──
  const handleEditAggravatingFactor = (af) => {
    setEditingAggravatingFactorId(af.aggravating_factor_id);
    setEditingAggravatingFactorValue(af.aggravating_factor);
  };
  const handleSaveAggravatingFactor = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/aggravating-factors/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ aggravating_factor: editingAggravatingFactorValue }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      setAggravatingFactors((prev) =>
        prev.map((af) =>
          af.aggravating_factor_id === id
            ? { ...af, aggravating_factor: editingAggravatingFactorValue }
            : af
        )
      );
      setEditingAggravatingFactorId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteAggravatingFactor = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/aggravating-factors/${id}`,
    idField: "aggravating_factor_id",
    setList: setAggravatingFactors,
    confirmMessage: "Delete this aggravating factor?",
    label: "Aggravating factor",
  });

  // ── Allergy Type edit/delete ──
  const handleEditAllergyType = (at) => {
    setEditingAllergyTypeId(at.allergy_type_id);
    setEditingAllergyTypeValue(at.allergy_type);
  };
  const handleSaveAllergyType = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/allergy-types/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ allergy_type: editingAllergyTypeValue }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      setAllergyTypes((prev) =>
        prev.map((at) =>
          at.allergy_type_id === id ? { ...at, allergy_type: editingAllergyTypeValue } : at
        )
      );
      setEditingAllergyTypeId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteAllergyType = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/allergy-types/${id}`,
    idField: "allergy_type_id",
    setList: setAllergyTypes,
    confirmMessage: "Delete this allergy type?",
    label: "Allergy type",
  });

  // ── Allergy edit/delete ──
  const handleEditAllergy = (a) => {
    setEditingAllergyId(a.allergy_id);
    setEditingAllergyData({ ...a });
  };
  const handleSaveAllergy = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/allergies/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          allergy_type_id: editingAllergyData.allergy_type_id,
          allergy: editingAllergyData.allergy,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      const typeName = allergyTypes.find(
        (at) => String(at.allergy_type_id) === String(editingAllergyData.allergy_type_id)
      )?.allergy_type;
      setAllergies((prev) =>
        prev.map((a) =>
          a.allergy_id === id
            ? { ...a, ...editingAllergyData, allergy_type: typeName }
            : a
        )
      );
      setEditingAllergyId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteAllergy = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/allergies/${id}`,
    idField: "allergy_id",
    setList: setAllergies,
    confirmMessage: "Delete this allergy?",
    label: "Allergy",
  });

  // ── Trauma Type edit/delete ──
  const handleEditTraumaType = (tt) => {
    setEditingTraumaTypeId(tt.trauma_type_id);
    setEditingTraumaTypeValue(tt.trauma_type);
  };
  const handleSaveTraumaType = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/trauma-types/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ trauma_type: editingTraumaTypeValue }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      setTraumaTypes((prev) =>
        prev.map((tt) =>
          tt.trauma_type_id === id ? { ...tt, trauma_type: editingTraumaTypeValue } : tt
        )
      );
      setEditingTraumaTypeId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteTraumaType = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/trauma-types/${id}`,
    idField: "trauma_type_id",
    setList: setTraumaTypes,
    confirmMessage: "Delete this trauma type?",
    label: "Trauma type",
  });

  const invBody = (d) => ({
    investigation_type_id: d.investigation_type_id,
    investigation_group_id: d.investigation_group_id || null,
    medical_specialty_ids: d.medical_specialty_ids || "",
    investigation_name: d.investigation_name,
    measurement_unit: d.measurement_unit || "",
    normal_range_lower: d.normal_range_lower === "" || d.normal_range_lower == null ? null : d.normal_range_lower,
    normal_range_upper: d.normal_range_upper === "" || d.normal_range_upper == null ? null : d.normal_range_upper,
    sos_range_lower: d.sos_range_lower === "" || d.sos_range_lower == null ? null : d.sos_range_lower,
    sos_range_upper: d.sos_range_upper === "" || d.sos_range_upper == null ? null : d.sos_range_upper,
  });

  // ── Trauma edit/delete ──
  const handleEditTrauma = (t) => { setEditingTraumaId(t.trauma_id); setEditingTraumaData({ ...t }); };
  const handleSaveTrauma = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/traumas/${id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ trauma_type_id: editingTraumaData.trauma_type_id, trauma_name: editingTraumaData.trauma_name }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      const typeName = traumaTypes.find((tt) => String(tt.trauma_type_id) === String(editingTraumaData.trauma_type_id))?.trauma_type;
      setTraumas((prev) => prev.map((t) => t.trauma_id === id ? { ...t, ...editingTraumaData, trauma_type: typeName } : t));
      setEditingTraumaId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteTrauma = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/traumas/${id}`,
    idField: "trauma_id",
    setList: setTraumas,
    confirmMessage: "Delete this trauma?",
    label: "Trauma",
  });

  // ── Addiction Category edit/delete ──
  const handleEditAddictionCategory = (ac) => { setEditingAddictionCategoryId(ac.addiction_category_id); setEditingAddictionCategoryValue(ac.addiction_category); };
  const handleSaveAddictionCategory = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/addiction-categories/${id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ addiction_category: editingAddictionCategoryValue }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      setAddictionCategories((prev) => prev.map((ac) => ac.addiction_category_id === id ? { ...ac, addiction_category: editingAddictionCategoryValue } : ac));
      setEditingAddictionCategoryId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteAddictionCategory = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/addiction-categories/${id}`,
    idField: "addiction_category_id",
    setList: setAddictionCategories,
    confirmMessage: "Delete this addiction category?",
    label: "Addiction category",
  });

  // ── Addiction Sub Category edit/delete ──
  const handleEditAddictionSubCategory = (sc) => {
    setEditingAddictionSubCategoryId(sc.addiction_sub_category_id);
    setEditingAddictionSubCategoryData({ ...sc });
  };
  const handleSaveAddictionSubCategory = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/addiction-sub-categories/${id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          addiction_category_id: editingAddictionSubCategoryData.addiction_category_id,
          addiction_sub_category: editingAddictionSubCategoryData.addiction_sub_category,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      const categoryName = addictionCategories.find(
        (ac) => String(ac.addiction_category_id) === String(editingAddictionSubCategoryData.addiction_category_id)
      )?.addiction_category;
      setAddictionSubCategories((prev) => prev.map((sc) => (
        sc.addiction_sub_category_id === id
          ? { ...sc, ...editingAddictionSubCategoryData, addiction_category: categoryName }
          : sc
      )));
      setEditingAddictionSubCategoryId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteAddictionSubCategory = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/addiction-sub-categories/${id}`,
    idField: "addiction_sub_category_id",
    setList: setAddictionSubCategories,
    confirmMessage: "Delete this addiction sub category?",
    label: "Addiction sub category",
  });

  // ── Addictions edit/delete ──
  const handleEditAddiction = (a) => { setEditingAddictionId(a.addictions_id); setEditingAddictionData({ ...a }); };
  const handleSaveAddiction = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/addictions/${id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          addiction_category_id: editingAddictionData.addiction_category_id,
          addiction_sub_category_id: editingAddictionData.addiction_sub_category_id,
          addictions: editingAddictionData.addictions,
          description: editingAddictionData.description,
          clinical_classification: editingAddictionData.clinical_classification,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      const listRes = await fetch(`${API_BASE}/master/addictions`);
      if (listRes.ok) setAddictionsList(await listRes.json());
      setEditingAddictionId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteAddiction = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/addictions/${id}`,
    idField: "addictions_id",
    setList: setAddictionsList,
    confirmMessage: "Delete this addiction?",
    label: "Addiction",
  });

  // ── Patient Condition edit/delete ──
  const handleEditPatientCondition = (pc) => { setEditingPatientConditionId(pc.patient_condition_id); setEditingPatientConditionValue(pc.patient_condition); };
  const handleSavePatientCondition = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/patient-conditions/${id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ patient_condition: editingPatientConditionValue }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      setPatientConditions((prev) => prev.map((pc) => pc.patient_condition_id === id ? { ...pc, patient_condition: editingPatientConditionValue } : pc));
      setEditingPatientConditionId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeletePatientCondition = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/patient-conditions/${id}`,
    idField: "patient_condition_id",
    setList: setPatientConditions,
    confirmMessage: "Delete this patient condition?",
    label: "Patient condition",
  });

  // ── Investigation Type edit/delete ──
  const handleEditInvestigationType = (it) => { setEditingInvestigationTypeId(it.investigation_type_id); setEditingInvestigationTypeValue(it.investigation_type); };
  const handleSaveInvestigationType = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/investigation-types/${id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ investigation_type: editingInvestigationTypeValue }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      setInvestigationTypes((prev) => prev.map((it) => it.investigation_type_id === id ? { ...it, investigation_type: editingInvestigationTypeValue } : it));
      setEditingInvestigationTypeId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteInvestigationType = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/investigation-types/${id}`,
    idField: "investigation_type_id",
    setList: setInvestigationTypes,
    confirmMessage: "Delete this investigation type?",
    label: "Investigation type",
  });

  // ── Investigation Group edit/delete ──
  const handleEditInvestigationGroup = (g) => { setEditingInvestigationGroupId(g.investigation_group_id); setEditingInvestigationGroupData({ ...g }); };
  const handleSaveInvestigationGroup = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/investigation-groups/${id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          investigation_type_id: editingInvestigationGroupData.investigation_type_id,
          investigation_group: editingInvestigationGroupData.investigation_group,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      const typeName = investigationTypes.find((it) => String(it.investigation_type_id) === String(editingInvestigationGroupData.investigation_type_id))?.investigation_type;
      setInvestigationGroups((prev) => prev.map((g) => g.investigation_group_id === id ? { ...g, ...editingInvestigationGroupData, investigation_type: typeName } : g));
      setEditingInvestigationGroupId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteInvestigationGroup = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/investigation-groups/${id}`,
    idField: "investigation_group_id",
    setList: setInvestigationGroups,
    confirmMessage: "Delete this investigation group?",
    label: "Investigation group",
  });

  // ── Investigation edit/delete ──
  const handleEditInvestigation = (inv) => { setEditingInvestigationId(inv.investigation_id); setEditingInvestigationData({ ...inv }); };
  const handleSaveInvestigation = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/investigations/${id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(invBody(editingInvestigationData)),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      const listRes = await fetch(`${API_BASE}/master/investigations`);
      if (listRes.ok) setInvestigations(await listRes.json());
      setEditingInvestigationId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteInvestigation = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/investigations/${id}`,
    idField: "investigation_id",
    setList: setInvestigations,
    confirmMessage: "Delete this investigation?",
    label: "Investigation",
  });

  // ── Symptom Category edit/delete ──
  const handleEditSymptomCategory = (sc) => {
    setEditingSymptomCategoryId(sc.symptom_category_id);
    setEditingSymptomCategoryValue(sc.symptom_category);
  };
  const handleSaveSymptomCategory = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/symptom-categories/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ symptom_category: editingSymptomCategoryValue }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      setSymptomCategories((prev) =>
        prev.map((sc) =>
          sc.symptom_category_id === id
            ? { ...sc, symptom_category: editingSymptomCategoryValue }
            : sc
        )
      );
      setEditingSymptomCategoryId(null);
      setSubmitMsg("Symptom category updated successfully!");
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteSymptomCategory = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/symptom-categories/${id}`,
    idField: "symptom_category_id",
    setList: setSymptomCategories,
    confirmMessage: "Delete this symptom category?",
    label: "Symptom category",
  });

  // ── Symptom edit/delete ──
  const handleEditSymptom = (s) => {
    setEditingSymptomId(s.symptom_id);
    setEditingSymptomData({ ...s });
  };
  const handleSaveSymptom = async (id) => {
    try {
      const fd = new FormData();
      fd.append("symptom_category_id", editingSymptomData.symptom_category_id);
      fd.append("symptom_name", editingSymptomData.symptom_name);
      fd.append("common_name", editingSymptomData.common_name || "");
      fd.append("severity_level", editingSymptomData.severity_level || "");
      fd.append("acute_chronic", editingSymptomData.acute_chronic || "");
      fd.append("body_system", editingSymptomData.body_system || "");
      fd.append("icd10_reference", editingSymptomData.icd10_reference || "");
      fd.append("clinical_notes", editingSymptomData.clinical_notes || "");
      fd.append("description", editingSymptomData.description || "");
      if (editingSymptomData.picture instanceof File) fd.append("picture", editingSymptomData.picture);
      const r = await fetch(`${API_BASE}/master/symptoms/${id}`, { method: "PUT", body: fd });
      if (!r.ok) throw new Error((await r.json()).message);
      const listRes = await fetch(`${API_BASE}/master/symptoms`);
      if (listRes.ok) setSymptoms(await listRes.json());
      setEditingSymptomId(null);
      setSubmitMsg("Symptom updated successfully!");
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteSymptom = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/symptoms/${id}`,
    idField: "symptom_id",
    setList: setSymptoms,
    confirmMessage: "Delete this symptom?",
    label: "Symptom",
  });

  // ── Establishment Master edit/delete ──
  const handleEditEstb = (est) => {
    const mapped = mapEstablishmentToProfileForm(est);
    setEditingEstbId(est.establishment_id);
    setEditingEstbData(mapped);
    setEstablishmentForm(mapped);
  };
  const handleSaveEstb = async (id) => {
    try {
      const existing = establishments.find((e) => String(e.establishment_id) === String(id));
      const merged = {
        ...(existing ? mapEstablishmentToProfileForm(existing) : {}),
        ...editingEstbData,
        establishment_id: id,
      };
      const fd = buildEstablishmentFormData(merged);
      const r = await fetch(`${API_BASE}/master/establishments/${id}`, { method: "PUT", body: fd });
      if (!r.ok) throw new Error(await parseApiError(r));
      const result = await r.json();
      const refreshed = await fetch(`${API_BASE}/master/establishments`).then((res) => res.json());
      setEstablishments(refreshed);
      setEditingEstbId(null);
      setSubmitMsg("Establishment updated successfully!");
      return result;
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteEstb = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/establishments/${id}`,
    idField: "establishment_id",
    setList: setEstablishments,
    confirmMessage: "Delete this establishment?",
    label: "Establishment",
  });

  // ── Doctor Master edit/delete ──
  const handleEditDoctor = (doc) => {
    const mapped = mapDoctorToProfileForm(doc);
    setEditingDoctorId(doc.doctor_id);
    setEditingDoctorData(mapped);
    setDoctorForm(mapped);
  };

  const navigateToDoctorCreateLogin = (doc) => {
    if (!doc) return;
    setCreateLoginEstb(null);
    setCreateLoginDoctor(doc);
    setDoctorLoginForm(buildDoctorLoginForm(doc));
    fetchDoctorLogins(doc.doctor_id);
    setCurrentStep(65);
    setSidebarOpen(false);
    setExpandedGroups({ "user-mgmt": true });
    setExpandedSections({ "user-mgmt:doctor-admin": true });
  };
  const handleSaveDoctor = async (id) => {
    try {
      const existing = doctors.find((d) => String(d.doctor_id) === String(id));
      const merged = {
        ...(existing ? mapDoctorToProfileForm(existing) : {}),
        ...editingDoctorData,
        doctor_id: id,
      };
      const fd = buildDoctorFormData(merged);
      const r = await fetch(`${API_BASE}/master/doctors/${id}`, { method: "PUT", body: fd });
      if (!r.ok) throw new Error(await parseApiError(r));
      const refreshed = await fetch(`${API_BASE}/master/doctors`).then((res) => res.json());
      setDoctors(refreshed);
      setEditingDoctorId(null);
      setSubmitMsg("Doctor updated successfully!");
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteDoctor = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/doctors/${id}`,
    idField: "doctor_id",
    setList: setDoctors,
    confirmMessage: "Delete this doctor?",
    label: "Doctor",
  });

  // ── Patient Master edit/delete ──
  const handleEditPatient = (patient) => {
    setEditingPatientId(patient.patient_id);
    setPatientForm(mapPatientToForm(patient));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const handleCancelPatientEdit = () => {
    setEditingPatientId(null);
    setPatientForm(emptyPatientForm());
  };
  const handleDeletePatient = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/patients/${id}`,
    idField: "patient_id",
    setList: setPatients,
    confirmMessage: "Delete this patient?",
    label: "Patient",
  });

  // ── User Master edit/delete ──
  const handleEditUser = (user) => {
    const mapped = mapUserToForm(user);
    if (!mapped.country_code && mapped.country_id) {
      const country = locations.find(
        (l) => String(l.location_id) === String(mapped.country_id)
      );
      mapped.country_code = country?.country_code || "";
    }
    setEditingUserId(user.user_id);
    setUserForm(mapped);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const handleCancelUserEdit = () => {
    setEditingUserId(null);
    setUserForm(emptyUserForm());
  };
  const handleDeleteUser = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/users/${id}`,
    idField: "user_id",
    setList: setUsers,
    confirmMessage: "Delete this user?",
    label: "User",
  });

  const fetchEstablishmentLogins = useCallback(async (establishmentId) => {
    try {
      if (establishmentId) {
        const r = await fetch(`${API_BASE}/master/logins?establishment_id=${establishmentId}`);
        if (!r.ok) throw new Error(`Server error ${r.status}`);
        const data = await r.json();
        setEstablishmentLogins(Array.isArray(data) ? data : []);
        return;
      }

      const r = await fetch(`${API_BASE}/master/logins`);
      if (!r.ok) throw new Error(`Server error ${r.status}`);
      const data = await r.json();
      const hospitalTypeIds = new Set(
        userTypes
          .filter((ut) => {
            const name = (ut.user_type || "").toLowerCase();
            return name.includes("hospital") || name.includes("establishment");
          })
          .map((ut) => Number(ut.user_type_id))
      );
      const filtered = (Array.isArray(data) ? data : []).filter((log) =>
        hospitalTypeIds.has(Number(log.user_type_id)) || log.establishment_id
      );
      setEstablishmentLogins(filtered);
    } catch (e) {
      console.error("Failed to load establishment logins:", e.message);
      setEstablishmentLogins([]);
    }
  }, [userTypes]);

  const buildEstablishmentLoginForm = (est) => {
    const hospitalUserType = userTypes.find((ut) => {
      const name = (ut.user_type || "").toLowerCase();
      return name.includes("hospital") || name.includes("establishment");
    });
    return {
      user_name: est.establishment_name || est.legal_entity_name || "",
      establishment_id: est.establishment_id,
      user_type_id: hospitalUserType?.user_type_id || userTypes[0]?.user_type_id || "",
      mobile_no: "",
      email_address: "",
      country_id: "",
      country_code: "",
      location_ids: [],
      payout_percentage: "",
      password: generateSystemPassword(),
    };
  };

  const handleCreateLoginEstablishmentChange = (establishmentId) => {
    if (!establishmentId) {
      setCreateLoginEstb(null);
      setEstablishmentLoginForm({ password: generateSystemPassword() });
      fetchEstablishmentLogins(null);
      return;
    }
    setCreateLoginDoctor(null);
    const est = establishments.find((e) => String(e.establishment_id) === String(establishmentId));
    if (!est) return;
    setCreateLoginEstb(est);
    setEstablishmentLoginForm(buildEstablishmentLoginForm(est));
    fetchEstablishmentLogins(est.establishment_id);
  };

  const handleOpenCreateLogin = (est) => {
    setCreateLoginEstb(est);
    setCreateLoginDoctor(null);
    setEstablishmentLoginForm(buildEstablishmentLoginForm(est));
    fetchEstablishmentLogins(est.establishment_id);
    setCurrentStep(45);
    setSidebarOpen(false);
    setExpandedGroups({ "user-mgmt": true });
    setExpandedSections({ "user-mgmt:hospital-admin": true });
  };

  const buildUserLoginFormFromUser = (user) => ({
    user_name: user.user_name || "",
    user_type_id: user.user_type_id || "",
    mobile_no: user.phone_number || "",
    email_address: user.email_address || "",
    establishment_id: user.establishment_id || "",
    payout_percentage: "",
    password: generateSystemPassword(),
  });

  const fetchUserLogins = useCallback(async (user) => {
    if (!user?.user_type_id) {
      setUserLogins([]);
      return;
    }
    try {
      const r = await fetch(`${API_BASE}/master/logins`);
      if (!r.ok) throw new Error(`Server error ${r.status}`);
      const data = await r.json();
      const phone = String(user.phone_number || "").trim();
      const name = String(user.user_name || "").trim();
      const filtered = (Array.isArray(data) ? data : []).filter((log) => {
        if (String(log.user_type_id) !== String(user.user_type_id)) return false;
        if (phone && String(log.mobile_no || "").trim() === phone) return true;
        return name && String(log.user_name || "").trim() === name;
      });
      setUserLogins(filtered);
    } catch (e) {
      console.error("Failed to load user logins:", e.message);
      setUserLogins([]);
    }
  }, []);

  const handleOpenCreateUserLogin = (user) => {
    if (!user) return;
    setCreateLoginEstb(null);
    setCreateLoginDoctor(null);
    setCreateLoginUser(user);
    setUserLoginForm(buildUserLoginFormFromUser(user));
    fetchUserLogins(user);
    setSubmitMsg("");
    setCurrentStep(69);
    setSidebarOpen(false);
    setExpandedGroups({ "user-mgmt": true });
    setExpandedSections({ "user-mgmt:user-admin": true });
  };

  const handleCreateLoginUserChange = (userId) => {
    if (!userId) {
      setCreateLoginUser(null);
      setUserLoginForm({ password: generateSystemPassword() });
      setUserLogins([]);
      return;
    }
    const user = users.find((u) => String(u.user_id) === String(userId));
    if (!user) return;
    setCreateLoginUser(user);
    setUserLoginForm(buildUserLoginFormFromUser(user));
    fetchUserLogins(user);
  };

  const handleCloseUserLogin = () => {
    setCreateLoginUser(null);
    setUserLoginForm({});
    setUserLogins([]);
  };

  const handleUserLoginChange = (field, value) => {
    setUserLoginForm((p) => ({ ...p, [field]: value }));
  };

  const handleSaveUserLogin = async () => {
    const { login_id, user_type_id, mobile_no, password, user_name } = userLoginForm;
    const isEditing = !!login_id;
    if (!user_type_id || !user_name?.trim() || !mobile_no?.trim() || (!isEditing && !password)) {
      setSubmitMsg("Error: User type, name, phone number, and password are required.");
      return;
    }
    setSavingUserLogin(true);
    try {
      const payload = {
        user_type_id,
        mobile_no,
        user_name,
        email_address: userLoginForm.email_address || "",
      };
      if (!isEditing || password) payload.password = password;
      if (createLoginUser?.establishment_id) {
        payload.establishment_id = createLoginUser.establishment_id;
      }
      const r = await fetch(
        isEditing ? `${API_BASE}/master/logins/${login_id}` : `${API_BASE}/master/logins`,
        {
          method: isEditing ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const data = await r.json();
      if (!r.ok) throw new Error(data.message);
      const loginId = data.login_id || login_id;
      if (password) {
        setLoginPasswordsById((p) => ({ ...p, [loginId]: password }));
      }
      await fetchUserLogins(createLoginUser);
      setUserLoginForm((prev) => {
        const { login_id: _omit, ...rest } = prev;
        return {
          ...rest,
          password: generateSystemPassword(),
        };
      });
      setSubmitMsg(
        isEditing
          ? `Login updated for ${createLoginUser?.user_name || "user"}.`
          : `Login created for ${createLoginUser?.user_name || "user"}.`
      );
    } catch (e) {
      setSubmitMsg(`Error: ${e.message}`);
    } finally {
      setSavingUserLogin(false);
    }
  };

  const handleEditUserLogin = (log) => {
    if (!log) return;
    setUserLoginForm({
      login_id: log.login_id,
      user_name: log.user_name || "",
      user_type_id: log.user_type_id || "",
      mobile_no: log.mobile_no || "",
      email_address: log.email_address || "",
      establishment_id: log.establishment_id || createLoginUser?.establishment_id || "",
      payout_percentage: log.payout_percentage ?? "",
      password: loginPasswordsById[log.login_id] || "",
    });
    setSubmitMsg("");
  };

  const handleCancelUserLoginEdit = () => {
    if (createLoginUser) {
      setUserLoginForm(buildUserLoginFormFromUser(createLoginUser));
    } else {
      setUserLoginForm({ password: generateSystemPassword() });
    }
    setSubmitMsg("");
  };

  const handleDeleteUserLogin = async (loginId) => {
    if (!window.confirm("Delete this user login?")) return;
    setSubmitMsg("");
    try {
      const r = await fetch(`${API_BASE}/master/logins/${loginId}`, { method: "DELETE" });
      if (!r.ok) throw new Error(await parseApiError(r));
      setUserLogins((prev) => prev.filter((item) => String(item.login_id) !== String(loginId)));
      setLoginPasswordsById((p) => {
        const next = { ...p };
        delete next[loginId];
        return next;
      });
      if (String(userLoginForm.login_id) === String(loginId)) {
        handleCancelUserLoginEdit();
      }
      setSubmitMsg("User login deleted successfully.");
    } catch (e) {
      setSubmitMsg(`Error: ${e.message}`);
    }
  };

  const buildLocationLoginForm = () => {
    const locationUserType = userTypes.find((ut) => /location/i.test(ut.user_type || ""));
    return {
      user_name: "",
      user_type_id: locationUserType?.user_type_id || "",
      mobile_no: "",
      email_address: "",
      country_id: "",
      country_code: "",
      location_ids: [],
      payout_percentage: "",
      password: generateSystemPassword(),
    };
  };

  const findCountryIdForLocationIds = (locationIds = []) => {
    if (!Array.isArray(locationIds) || locationIds.length === 0) return "";
    const countries = locations.filter((location) =>
      /country/i.test(location.location_type || "")
    );
    for (const country of countries) {
      const underCountry = locationIds.some((id) =>
        isLocationUnderParent(locations, id, country.location_id)
      );
      if (underCountry) return country.location_id;
    }
    return "";
  };

  const fetchLocationLogins = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/master/logins`);
      if (!r.ok) throw new Error(`Server error ${r.status}`);
      const data = await r.json();
      const locationTypeIds = new Set(
        userTypes
          .filter((ut) => /location/i.test(ut.user_type || ""))
          .map((ut) => Number(ut.user_type_id))
      );
      const filtered = (Array.isArray(data) ? data : []).filter((log) =>
        locationTypeIds.has(Number(log.user_type_id))
      );
      setLocationLogins(filtered);
    } catch (e) {
      console.error("Failed to load location admin logins:", e.message);
      setLocationLogins([]);
    }
  }, [userTypes]);

  const fetchDoctorLogins = useCallback(async (doctorId) => {
    try {
      if (doctorId) {
        const r = await fetch(`${API_BASE}/master/logins?doctor_id=${doctorId}`);
        if (!r.ok) throw new Error(`Server error ${r.status}`);
        const data = await r.json();
        setDoctorLogins(Array.isArray(data) ? data : []);
        return;
      }

      const r = await fetch(`${API_BASE}/master/logins`);
      if (!r.ok) throw new Error(`Server error ${r.status}`);
      const data = await r.json();
      const doctorTypeIds = new Set(
        userTypes
          .filter((ut) => /doctor/i.test(ut.user_type || ""))
          .map((ut) => Number(ut.user_type_id))
      );
      const filtered = (Array.isArray(data) ? data : []).filter((log) =>
        doctorTypeIds.has(Number(log.user_type_id)) || log.doctor_id
      );
      setDoctorLogins(filtered);
    } catch (e) {
      console.error("Failed to load doctor logins:", e.message);
      setDoctorLogins([]);
    }
  }, [userTypes]);

  useEffect(() => {
    if (currentStep === 64) {
      setCreateLoginEstb(null);
      setCreateLoginDoctor(null);
      setLocationLoginForm((prev) => {
        // Keep an in-progress edit; only initialize/reset the create form.
        if (prev?.login_id) return prev;
        return {
          ...buildLocationLoginForm(),
          password: prev.password || generateSystemPassword(),
        };
      });
      fetchLocationLogins();
    }
  }, [currentStep, fetchLocationLogins, userTypes]);

  useEffect(() => {
    if (currentStep === 45) {
      setCreateLoginDoctor(null);
      fetchEstablishmentLogins(establishmentLoginForm.establishment_id || null);
    }
  }, [currentStep, establishmentLoginForm.establishment_id, fetchEstablishmentLogins]);

  useEffect(() => {
    if (currentStep === 65) {
      setCreateLoginEstb(null);
      fetchDoctorLogins(doctorLoginForm.doctor_id || null);
    }
  }, [currentStep, doctorLoginForm.doctor_id, fetchDoctorLogins]);

  useEffect(() => {
    if (currentStep === 69 && !createLoginUser) {
      setCreateLoginEstb(null);
      setCreateLoginDoctor(null);
      setUserLoginForm((prev) => ({
        password: prev.password || generateSystemPassword(),
      }));
      setUserLogins([]);
    }
  }, [currentStep, createLoginUser]);

  const buildDoctorLoginForm = (doc) => {
    const doctorUserType = userTypes.find((ut) => {
      const name = (ut.user_type || "").toLowerCase();
      return name.includes("doctor");
    });
    return {
      user_name: doc.doctor_name || "",
      doctor_id: doc.doctor_id,
      user_type_id: doctorUserType?.user_type_id || "",
      mobile_no: "",
      email_address: doc.email_address || "",
      payout_percentage: "",
      password: generateSystemPassword(),
    };
  };

  const handleOpenCreateDoctorLogin = (doc) => {
    navigateToDoctorCreateLogin(doc);
  };

  const handleCreateLoginDoctorChange = (doctorId) => {
    if (!doctorId) {
      setCreateLoginDoctor(null);
      setDoctorLoginForm({ password: generateSystemPassword() });
      fetchDoctorLogins(null);
      return;
    }
    setCreateLoginEstb(null);
    const doc = doctors.find((d) => String(d.doctor_id) === String(doctorId));
    if (!doc) return;
    setCreateLoginDoctor(doc);
    setDoctorLoginForm(buildDoctorLoginForm(doc));
    fetchDoctorLogins(doc.doctor_id);
  };

  const handleDoctorLoginChange = (field, value) => {
    setDoctorLoginForm((p) => ({ ...p, [field]: value }));
  };

  const handleLocationLoginChange = (field, value) => {
    setLocationLoginForm((p) => ({ ...p, [field]: value }));
  };

  const handleEditLocationLogin = (log) => {
    if (!log) return;
    const locationIds = Array.isArray(log.location_ids) ? log.location_ids : [];
    const countryId = findCountryIdForLocationIds(locationIds);
    const country = locations.find(
      (location) => String(location.location_id) === String(countryId)
    );
    setLocationLoginForm({
      login_id: log.login_id,
      user_name: log.user_name || "",
      user_type_id: log.user_type_id || "",
      mobile_no: log.mobile_no || "",
      email_address: log.email_address || "",
      country_id: countryId || "",
      country_code: log.country_code || country?.country_code || "",
      location_ids: locationIds.map((id) => Number(id)),
      payout_percentage: log.payout_percentage ?? "",
      // Keep password empty on edit so it is never regenerated or sent in the update payload.
      password: "",
    });
    setSubmitMsg("");
  };

  const handleCancelLocationLoginEdit = () => {
    setLocationLoginForm({
      ...buildLocationLoginForm(),
      password: generateSystemPassword(),
    });
    setSubmitMsg("");
  };

  const handleDeleteLocationLogin = async (loginId) => {
    if (!window.confirm("Delete this location admin login?")) return;
    setSubmitMsg("");
    try {
      const r = await fetch(`${API_BASE}/master/logins/${loginId}`, { method: "DELETE" });
      if (!r.ok) throw new Error(await parseApiError(r));
      setLocationLogins((prev) => prev.filter((item) => String(item.login_id) !== String(loginId)));
      setLoginPasswordsById((p) => {
        const next = { ...p };
        delete next[loginId];
        return next;
      });
      if (String(locationLoginForm.login_id) === String(loginId)) {
        handleCancelLocationLoginEdit();
      }
      setSubmitMsg("Location admin login deleted successfully.");
    } catch (e) {
      setSubmitMsg(`Error: ${e.message}`);
    }
  };

  const handleSaveLocationLogin = async () => {
    const {
      login_id,
      user_type_id,
      mobile_no,
      password,
      user_name,
      country_id,
      location_ids = [],
    } = locationLoginForm;
    const isEditing = !!login_id;
    if (!user_type_id || !user_name?.trim() || !mobile_no?.trim() || (!isEditing && !password)) {
      setSubmitMsg("Error: User type, name, phone number, and password are required.");
      return;
    }
    if (!country_id) {
      setSubmitMsg("Error: Country is required.");
      return;
    }
    if (location_ids.length === 0) {
      setSubmitMsg("Error: Select at least one City/District location.");
      return;
    }
    setSavingLocationLogin(true);
    try {
      const payload = { ...locationLoginForm };
      // Keep country_id so backend can create user_kpi rows per selected location.
      delete payload.login_id;
      // Editing must never change the stored password.
      if (isEditing) delete payload.password;
      const r = await fetch(
        isEditing ? `${API_BASE}/master/logins/${login_id}` : `${API_BASE}/master/logins`,
        {
          method: isEditing ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const data = await r.json();
      if (!r.ok) throw new Error(data.message);
      const loginId = data.login_id || login_id;
      if (!isEditing && password) {
        setLoginPasswordsById((p) => ({ ...p, [loginId]: password }));
      }
      await fetchLocationLogins();
      setLocationLoginForm({
        ...buildLocationLoginForm(),
        password: generateSystemPassword(),
      });
      setSubmitMsg(
        isEditing
          ? "Location admin login updated successfully."
          : "Location admin login created successfully."
      );
    } catch (e) {
      setSubmitMsg(`Error: ${e.message}`);
    } finally {
      setSavingLocationLogin(false);
    }
  };

  const handleEditDoctorLogin = (log) => {
    if (!log) return;
    if (log.doctor_id) {
      const doc = doctors.find((d) => String(d.doctor_id) === String(log.doctor_id));
      if (doc) setCreateLoginDoctor(doc);
    }
    setDoctorLoginForm({
      login_id: log.login_id,
      user_name: log.user_name || "",
      doctor_id: log.doctor_id || "",
      user_type_id: log.user_type_id || "",
      mobile_no: log.mobile_no || "",
      email_address: log.email_address || "",
      payout_percentage: log.payout_percentage ?? "",
      password: loginPasswordsById[log.login_id] || "",
    });
    setSubmitMsg("");
  };

  const handleCancelDoctorLoginEdit = () => {
    const doc = createLoginDoctor;
    setDoctorLoginForm(doc ? buildDoctorLoginForm(doc) : { password: generateSystemPassword() });
    setSubmitMsg("");
  };

  const handleDeleteDoctorLogin = async (loginId) => {
    if (!window.confirm("Delete this doctor login?")) return;
    setSubmitMsg("");
    try {
      const r = await fetch(`${API_BASE}/master/logins/${loginId}`, { method: "DELETE" });
      if (!r.ok) throw new Error(await parseApiError(r));
      setDoctorLogins((prev) => prev.filter((item) => String(item.login_id) !== String(loginId)));
      setLoginPasswordsById((p) => {
        const next = { ...p };
        delete next[loginId];
        return next;
      });
      if (String(doctorLoginForm.login_id) === String(loginId)) {
        handleCancelDoctorLoginEdit();
      }
      setSubmitMsg("Doctor login deleted successfully.");
    } catch (e) {
      setSubmitMsg(`Error: ${e.message}`);
    }
  };

  const handleSaveDoctorLogin = async () => {
    const { login_id, user_type_id, mobile_no, password, user_name } = doctorLoginForm;
    const isEditing = !!login_id;
    if (!user_type_id || !user_name?.trim() || !mobile_no?.trim() || (!isEditing && !password)) {
      setSubmitMsg("Error: User type, name, phone number, and password are required.");
      return;
    }
    setSavingDoctorLogin(true);
    try {
      const payload = { ...doctorLoginForm };
      delete payload.login_id;
      if (isEditing && !password) delete payload.password;
      const r = await fetch(
        isEditing ? `${API_BASE}/master/logins/${login_id}` : `${API_BASE}/master/logins`,
        {
          method: isEditing ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const data = await r.json();
      if (!r.ok) throw new Error(data.message);
      const loginId = data.login_id || login_id;
      if (password) {
        setLoginPasswordsById((p) => ({ ...p, [loginId]: password }));
      }
      await fetchDoctorLogins(doctorLoginForm.doctor_id);
      setDoctorLoginForm((p) => {
        const { login_id: _omit, ...rest } = p;
        return {
          ...rest,
          mobile_no: "",
          email_address: "",
          password: generateSystemPassword(),
        };
      });
      const docName = doctors.find(
        (d) => String(d.doctor_id) === String(doctorLoginForm.doctor_id)
      )?.doctor_name;
      setSubmitMsg(
        isEditing
          ? `Login updated for ${docName || "doctor"}.`
          : `Login created for ${docName || "doctor"}.`
      );
    } catch (e) {
      setSubmitMsg(`Error: ${e.message}`);
    } finally {
      setSavingDoctorLogin(false);
    }
  };

  const handleCloseCreateLogin = () => {
    setCreateLoginEstb(null);
    setCreateLoginDoctor(null);
    setEstablishmentLoginForm({ password: generateSystemPassword() });
    setDoctorLoginForm({ password: generateSystemPassword() });
    setEstablishmentLogins([]);
    setDoctorLogins([]);
    setLoginPasswordsById({});
    setSavingEstablishmentLogin(false);
    setSavingDoctorLogin(false);
  };

  const handleEstablishmentLoginChange = (field, value) => {
    setEstablishmentLoginForm((p) => ({ ...p, [field]: value }));
  };

  const handleEditEstablishmentLogin = (log) => {
    if (!log) return;
    if (log.establishment_id) {
      const est = establishments.find(
        (e) => String(e.establishment_id) === String(log.establishment_id)
      );
      if (est) setCreateLoginEstb(est);
    }
    setEstablishmentLoginForm({
      login_id: log.login_id,
      user_name: log.user_name || "",
      establishment_id: log.establishment_id || "",
      user_type_id: log.user_type_id || "",
      mobile_no: log.mobile_no || "",
      email_address: log.email_address || "",
      country_id: "",
      country_code: "",
      location_ids: [],
      payout_percentage: log.payout_percentage ?? "",
      password: loginPasswordsById[log.login_id] || "",
    });
    setSubmitMsg("");
  };

  const handleCancelEstablishmentLoginEdit = () => {
    const est = createLoginEstb;
    setEstablishmentLoginForm(
      est ? buildEstablishmentLoginForm(est) : { password: generateSystemPassword() }
    );
    setSubmitMsg("");
  };

  const handleDeleteEstablishmentLogin = async (loginId) => {
    if (!window.confirm("Delete this establishment login?")) return;
    setSubmitMsg("");
    try {
      const r = await fetch(`${API_BASE}/master/logins/${loginId}`, { method: "DELETE" });
      if (!r.ok) throw new Error(await parseApiError(r));
      setEstablishmentLogins((prev) =>
        prev.filter((item) => String(item.login_id) !== String(loginId))
      );
      setLoginPasswordsById((p) => {
        const next = { ...p };
        delete next[loginId];
        return next;
      });
      if (String(establishmentLoginForm.login_id) === String(loginId)) {
        handleCancelEstablishmentLoginEdit();
      }
      setSubmitMsg("Establishment login deleted successfully.");
    } catch (e) {
      setSubmitMsg(`Error: ${e.message}`);
    }
  };

  const handleSaveEstablishmentLogin = async () => {
    const { login_id, user_type_id, mobile_no, password, user_name } = establishmentLoginForm;
    const isEditing = !!login_id;
    if (!user_type_id || !user_name?.trim() || !mobile_no?.trim() || (!isEditing && !password)) {
      setSubmitMsg("Error: User type, name, phone number, and password are required.");
      return;
    }
    setSavingEstablishmentLogin(true);
    try {
      const payload = { ...establishmentLoginForm };
      delete payload.login_id;
      delete payload.country_id;
      if (isEditing && !password) delete payload.password;
      const r = await fetch(
        isEditing ? `${API_BASE}/master/logins/${login_id}` : `${API_BASE}/master/logins`,
        {
          method: isEditing ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const data = await r.json();
      if (!r.ok) throw new Error(data.message);
      const loginId = data.login_id || login_id;
      if (password) {
        setLoginPasswordsById((p) => ({ ...p, [loginId]: password }));
      }
      await fetchEstablishmentLogins(establishmentLoginForm.establishment_id);
      setEstablishmentLoginForm((p) => {
        const { login_id: _omit, ...rest } = p;
        return {
          ...rest,
          mobile_no: "",
          email_address: "",
          password: generateSystemPassword(),
        };
      });
      const estName = establishments.find(
        (e) => String(e.establishment_id) === String(establishmentLoginForm.establishment_id)
      )?.establishment_name;
      setSubmitMsg(
        isEditing
          ? `Login updated for ${estName || "establishment"}.`
          : `Login created for ${estName || "establishment"}.`
      );
    } catch (e) {
      setSubmitMsg(`Error: ${e.message}`);
    } finally {
      setSavingEstablishmentLogin(false);
    }
  };

  // ── Event Type edit/delete ──
  const handleEditEventType = (et) => {
    setEditingEventTypeId(et.event_type_id);
    setEditingEventTypeValue(et.event_type);
  };
  const handleSaveEventType = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/event-types/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ event_type: editingEventTypeValue }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      setEventTypes((prev) =>
        prev.map((et) =>
          et.event_type_id === id ? { ...et, event_type: editingEventTypeValue } : et
        )
      );
      setEditingEventTypeId(null);
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteEventType = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/event-types/${id}`,
    idField: "event_type_id",
    setList: setEventTypes,
    confirmMessage: "Delete this event type?",
    label: "Event type",
  });

  // ── Immunization edit/delete ──
  const morbidityDiseases = getMorbidityDiseases(diseases);

  const handleEditImmunization = (imm) => {
    setEditingImmunizationId(imm.immunization_id);
    setEditingImmunizationData({ ...imm });
  };
  const handleSaveImmunization = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/immunizations/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          immunization_name: editingImmunizationData.immunization_name,
          immunization_type: editingImmunizationData.immunization_type,
          applicable_gender: editingImmunizationData.applicable_gender,
          applicable_age_group: editingImmunizationData.applicable_age_group,
          applicable_morbidity_id: editingImmunizationData.applicable_morbidity_id,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      const morbidityName = morbidityDiseases.find(
        (d) => String(d.disease_id) === String(editingImmunizationData.applicable_morbidity_id)
      )?.disease_name;
      setImmunizations((prev) =>
        prev.map((imm) =>
          imm.immunization_id === id
            ? { ...imm, ...editingImmunizationData, applicable_morbidity: morbidityName }
            : imm
        )
      );
      setEditingImmunizationId(null);
      setSubmitMsg("Immunization updated successfully!");
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteImmunization = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/immunizations/${id}`,
    idField: "immunization_id",
    setList: setImmunizations,
    confirmMessage: "Delete this immunization?",
    label: "Immunization",
  });

  // ── Vaccine edit/delete ──
  const handleEditVaccine = (v) => {
    setEditingVaccineId(v.vaccine_id);
    setEditingVaccineData({ ...v });
  };
  const handleSaveVaccine = async (id) => {
    try {
      const r = await fetch(`${API_BASE}/master/vaccines/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          age_group: editingVaccineData.age_group,
          gender: editingVaccineData.gender,
          vaccine_name: editingVaccineData.vaccine_name,
          schedule_detail: editingVaccineData.schedule_detail,
        }),
      });
      if (!r.ok) throw new Error((await r.json()).message);
      setVaccines((prev) =>
        prev.map((v) => (v.vaccine_id === id ? { ...v, ...editingVaccineData } : v))
      );
      setEditingVaccineId(null);
      setSubmitMsg("Vaccine updated successfully!");
    } catch (e) { setSubmitMsg(`Error: ${e.message}`); }
  };
  const handleDeleteVaccine = makeDeleteHandler({
    buildUrl: (id) => `${API_BASE}/master/vaccines/${id}`,
    idField: "vaccine_id",
    setList: setVaccines,
    confirmMessage: "Delete this vaccine?",
    label: "Vaccine",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;
    if (form.checkValidity() === false) { setValidated(true); return; }
    setSubmitMsg("");

    const post = async (url, body) => {
      const r = await fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      const ct = r.headers.get('content-type') || '';
      if (!ct.includes('application/json')) throw new Error('Backend not reachable — is the server running?');
      const data = await r.json();
      if (!r.ok) throw new Error(data.message);
      return data;
    };

    try {
      if (currentStep === 0) {
        const data = await post(`${API_BASE}/master/user-types`, userTypeForm);
        setUserTypes((prev) => [
          ...prev,
          { ...userTypeForm, user_type_id: data.user_type_id },
        ]);
        setUserTypeForm({});
        setSubmitMsg("User Type saved successfully!");
      } else if (currentStep === 1) {
        const data = await post(`${API_BASE}/locations/types`, locationTypeForm);
        setLocationTypes((prev) => [...prev, { location_type_id: data.location_type_id, location_type: locationTypeForm.location_type }]);
        setLocationTypeForm({});
        setSubmitMsg("Location Type saved successfully!");
      } else if (currentStep === 2) {
        const fd = new FormData();
        fd.append("location_type_id", locationForm.location_type_id);
        fd.append("parent_location_id", locationForm.parent_location_id || "");
        fd.append("location_name", locationForm.location_name);
        fd.append("location_category", locationForm.location_category || "");
        fd.append("country_code", locationForm.country_code || "");
        fd.append("treatment_cost_currency", locationForm.treatment_cost_currency || "");
        fd.append("local_currency", locationForm.local_currency || "");
        if (locationForm.icon_image instanceof File) fd.append("icon_image", locationForm.icon_image);
        if (locationForm.country_flag_image instanceof File) {
          fd.append("country_flag_image", locationForm.country_flag_image);
        } else if (locationForm.country_flag_image_path) {
          fd.append("country_flag_image_path", locationForm.country_flag_image_path);
        }
        const r = await fetch(`${API_BASE}/locations`, { method: "POST", body: fd });
        const ct = r.headers.get("content-type") || "";
        if (!ct.includes("application/json")) throw new Error("Backend not reachable — is the server running?");
        const data = await r.json();
        if (!r.ok) throw new Error(data.message);
        const listRes = await fetch(`${API_BASE}/locations`);
        if (listRes.ok) setLocations(await listRes.json());
        setLocationForm({});
        setSubmitMsg("Location saved successfully!");
      } else if (currentStep === 3) {
        const fd = new FormData();
        fd.append("medical_specialty", specialtyForm.medical_specialty);
        fd.append("medical_specialty_type", specialtyForm.medical_specialty_type);
        fd.append("mvt_relevance", specialtyForm.mvt_relevance || "No");
        if (specialtyForm.icon_image instanceof File) fd.append("icon_image", specialtyForm.icon_image);
        const r = await fetch(`${API_BASE}/master/specialties`, { method: "POST", body: fd });
        const ct = r.headers.get("content-type") || "";
        if (!ct.includes("application/json")) throw new Error("Backend not reachable — is the server running?");
        const data = await r.json();
        if (!r.ok) throw new Error(data.message);
        const listRes = await fetch(`${API_BASE}/master/specialties`);
        if (listRes.ok) setSpecialties(await listRes.json());
        setSpecialtyForm({});
        setSubmitMsg("Medical Specialty saved successfully!");
      } else if (currentStep === 4) {
        const data = await post(`${API_BASE}/master/medical-procedures`, medicalProcedureForm);
        const specialtyName = specialties.find(
          (s) => String(s.medical_specialty_id) === String(medicalProcedureForm.medical_specialty_id)
        )?.medical_specialty;
        setMedicalProcedures((prev) => [
          ...prev,
          { ...medicalProcedureForm, procedure_id: data.procedure_id, medical_specialty: specialtyName },
        ]);
        setMedicalProcedureForm({});
        setSubmitMsg("Medical Procedure saved successfully!");
      } else if (currentStep === 5) {
        const data = await post(`${API_BASE}/master/diseases`, diseaseForm);
        const specialtyName = specialties.find(
          (s) => String(s.medical_specialty_id) === String(diseaseForm.medical_specialty_id)
        )?.medical_specialty;
        setDiseases((prev) => [
          ...prev,
          { ...diseaseForm, disease_id: data.disease_id, medical_specialty: specialtyName },
        ]);
        setDiseaseForm({});
        setSubmitMsg("Disease saved successfully!");
      } else if (currentStep === 6) {
        const data = await post(`${API_BASE}/master/symptom-categories`, symptomCategoryForm);
        setSymptomCategories((prev) => [
          ...prev,
          { ...symptomCategoryForm, symptom_category_id: data.symptom_category_id },
        ]);
        setSymptomCategoryForm({});
        setSubmitMsg("Symptom Category saved successfully!");
      } else if (currentStep === 7) {
        const fd = new FormData();
        fd.append("symptom_category_id", symptomForm.symptom_category_id);
        fd.append("symptom_name", symptomForm.symptom_name);
        fd.append("common_name", symptomForm.common_name || "");
        fd.append("severity_level", symptomForm.severity_level || "");
        fd.append("acute_chronic", symptomForm.acute_chronic || "");
        fd.append("body_system", symptomForm.body_system || "");
        fd.append("icd10_reference", symptomForm.icd10_reference || "");
        fd.append("clinical_notes", symptomForm.clinical_notes || "");
        fd.append("description", symptomForm.description || "");
        if (symptomForm.picture instanceof File) fd.append("picture", symptomForm.picture);
        const r = await fetch(`${API_BASE}/master/symptoms`, { method: "POST", body: fd });
        const ct = r.headers.get("content-type") || "";
        if (!ct.includes("application/json")) throw new Error("Backend not reachable — is the server running?");
        const data = await r.json();
        if (!r.ok) throw new Error(data.message);
        const listRes = await fetch(`${API_BASE}/master/symptoms`);
        if (listRes.ok) setSymptoms(await listRes.json());
        setSymptomForm({});
        setSubmitMsg("Symptom saved successfully!");
      } else if (currentStep === 8) {
        const data = await post(`${API_BASE}/master/aggravating-factors`, aggravatingFactorForm);
        setAggravatingFactors((prev) => [
          ...prev,
          { ...aggravatingFactorForm, aggravating_factor_id: data.aggravating_factor_id },
        ]);
        setAggravatingFactorForm({});
        setSubmitMsg("Aggravating Factor saved successfully!");
      } else if (currentStep === 9) {
        const data = await post(`${API_BASE}/master/allergy-types`, allergyTypeForm);
        setAllergyTypes((prev) => [
          ...prev,
          { ...allergyTypeForm, allergy_type_id: data.allergy_type_id },
        ]);
        setAllergyTypeForm({});
        setSubmitMsg("Allergy Type saved successfully!");
      } else if (currentStep === 10) {
        const data = await post(`${API_BASE}/master/allergies`, allergyForm);
        const typeName = allergyTypes.find(
          (at) => String(at.allergy_type_id) === String(allergyForm.allergy_type_id)
        )?.allergy_type;
        setAllergies((prev) => [
          ...prev,
          { ...allergyForm, allergy_id: data.allergy_id, allergy_type: typeName },
        ]);
        setAllergyForm({});
        setSubmitMsg("Allergy saved successfully!");
      } else if (currentStep === 11) {
        const data = await post(`${API_BASE}/master/trauma-types`, traumaTypeForm);
        setTraumaTypes((prev) => [
          ...prev,
          { ...traumaTypeForm, trauma_type_id: data.trauma_type_id },
        ]);
        setTraumaTypeForm({});
        setSubmitMsg("Trauma Type saved successfully!");
      } else if (currentStep === 12) {
        const data = await post(`${API_BASE}/master/traumas`, traumaForm);
        const typeName = traumaTypes.find((tt) => String(tt.trauma_type_id) === String(traumaForm.trauma_type_id))?.trauma_type;
        setTraumas((prev) => [...prev, { ...traumaForm, trauma_id: data.trauma_id, trauma_type: typeName }]);
        setTraumaForm({});
        setSubmitMsg("Trauma saved successfully!");
      } else if (currentStep === 13) {
        const data = await post(`${API_BASE}/master/addiction-categories`, addictionCategoryForm);
        setAddictionCategories((prev) => [...prev, { ...addictionCategoryForm, addiction_category_id: data.addiction_category_id }]);
        setAddictionCategoryForm({});
        setSubmitMsg("Addiction Category saved successfully!");
      } else if (currentStep === 14) {
        const data = await post(`${API_BASE}/master/addiction-sub-categories`, addictionSubCategoryForm);
        const categoryName = addictionCategories.find(
          (ac) => String(ac.addiction_category_id) === String(addictionSubCategoryForm.addiction_category_id)
        )?.addiction_category;
        setAddictionSubCategories((prev) => [
          ...prev,
          { ...addictionSubCategoryForm, addiction_sub_category_id: data.addiction_sub_category_id, addiction_category: categoryName },
        ]);
        setAddictionSubCategoryForm({});
        setSubmitMsg("Addiction Sub Category saved successfully!");
      } else if (currentStep === 15) {
        const data = await post(`${API_BASE}/master/addictions`, addictionsForm);
        const listRes = await fetch(`${API_BASE}/master/addictions`);
        if (listRes.ok) setAddictionsList(await listRes.json());
        setAddictionsForm({});
        setSubmitMsg("Addiction saved successfully!");
      } else if (currentStep === 16) {
        const data = await post(`${API_BASE}/master/patient-conditions`, patientConditionForm);
        setPatientConditions((prev) => [...prev, { ...patientConditionForm, patient_condition_id: data.patient_condition_id }]);
        setPatientConditionForm({});
        setSubmitMsg("Patient Condition saved successfully!");
      } else if (currentStep === 17) {
        const data = await post(`${API_BASE}/master/investigation-types`, investigationTypeForm);
        setInvestigationTypes((prev) => [...prev, { ...investigationTypeForm, investigation_type_id: data.investigation_type_id }]);
        setInvestigationTypeForm({});
        setSubmitMsg("Investigation Type saved successfully!");
      } else if (currentStep === 18) {
        const data = await post(`${API_BASE}/master/investigation-groups`, investigationGroupForm);
        const typeName = investigationTypes.find((it) => String(it.investigation_type_id) === String(investigationGroupForm.investigation_type_id))?.investigation_type;
        setInvestigationGroups((prev) => [...prev, { ...investigationGroupForm, investigation_group_id: data.investigation_group_id, investigation_type: typeName }]);
        setInvestigationGroupForm({});
        setSubmitMsg("Investigation Group saved successfully!");
      } else if (currentStep === 19) {
        const data = await post(`${API_BASE}/master/investigations`, invBody(investigationForm));
        const listRes = await fetch(`${API_BASE}/master/investigations`);
        if (listRes.ok) setInvestigations(await listRes.json());
        setInvestigationForm({});
        setSubmitMsg("Investigation saved successfully!");
      } else if (currentStep === 20) {
        await post(`${API_BASE}/master/establishment-types`, { establishment_type: estTypeForm.establishment_type });
        await fetchEstablishmentTypes();
        setEstTypeForm({});
        setSubmitMsg("Establishment Type saved successfully!");
      } else if (currentStep === 21) {
        await post(`${API_BASE}/master/establishment-sub-types`, {
          establishment_type_id: establishmentSubTypeForm.establishment_type_id,
          establishment_sub_type: establishmentSubTypeForm.establishment_sub_type,
        });
        await fetchEstablishmentSubTypes();
        setEstablishmentSubTypeForm({});
        setSubmitMsg("Establishment Sub Type saved successfully!");
      } else if (currentStep === 22) {
        const data = await post(`${API_BASE}/master/degrees`, degreeForm);
        setDegrees((prev) => [...prev, { ...degreeForm, medical_degree_id: data.medical_degree_id }]);
        setDegreeForm({});
        setSubmitMsg("Medical Degree saved successfully!");
      } else if (currentStep === 23) {
        const data = await post(`${API_BASE}/master/specializations`, specializationForm);
        const typeName = specializationTypes.find(
          (st) => String(st.specialization_type_id) === String(specializationForm.specialization_type_id)
        )?.specialization_type;
        setSpecializations((prev) => [
          ...prev,
          { ...specializationForm, specialization_id: data.specialization_id, specialization_type: typeName },
        ]);
        setSpecializationForm({});
        setSubmitMsg("Specialization saved successfully!");
      } else if (currentStep === 24) {
        const data = await post(`${API_BASE}/master/specialization-types`, specializationTypeForm);
        setSpecializationTypes((prev) => [
          ...prev,
          { ...specializationTypeForm, specialization_type_id: data.specialization_type_id },
        ]);
        setSpecializationTypeForm({});
        setSubmitMsg("Specialization Type saved successfully!");
      } else if (currentStep === 25) {
        const data = await post(`${API_BASE}/master/accreditations`, accreditationForm);
        setAccreditations((prev) => [...prev, { ...accreditationForm, accreditation_id: data.accreditation_id }]);
        setAccreditationForm({});
        setSubmitMsg("Accreditation saved successfully!");
      } else if (currentStep === 26) {
        const data = await post(`${API_BASE}/master/department-categories`, departmentCategoryForm);
        setDepartmentCategories((prev) => [
          ...prev,
          { ...departmentCategoryForm, department_category_id: data.department_category_id },
        ]);
        setDepartmentCategoryForm({});
        setSubmitMsg("Department Type saved successfully!");
      } else if (currentStep === 27) {
        const data = await post(`${API_BASE}/master/departments`, departmentForm);
        const catName = departmentCategories.find(
          (dc) => String(dc.department_category_id) === String(departmentForm.department_category_id)
        )?.department_category;
        setDepartments((prev) => [
          ...prev,
          { ...departmentForm, department_id: data.department_id, department_category: catName },
        ]);
        setDepartmentForm({});
        setSubmitMsg("Hospital Department saved successfully!");
      } else if (currentStep === 28) {
        const data = await post(`${API_BASE}/master/designations`, designationForm);
        setDesignations((prev) => [
          ...prev,
          { ...designationForm, designation_id: data.designation_id },
        ]);
        setDesignationForm({});
        setSubmitMsg("Designation saved successfully!");
      } else if (currentStep === 29) {
        const data = await post(`${API_BASE}/master/profile-topics`, profileTopicForm);
        setProfileTopics((prev) => [
          ...prev,
          { ...profileTopicForm, profile_topic_id: data.profile_topic_id },
        ]);
        setProfileTopicForm({});
        setSubmitMsg("Profile Topic saved successfully!");
      } else if (currentStep === 30) {
        const data = await post(`${API_BASE}/master/profile-sub-topics`, profileSubTopicForm);
        const topicName = profileTopics.find(
          (pt) => String(pt.profile_topic_id) === String(profileSubTopicForm.profile_topic_id)
        )?.profile_topic;
        setProfileSubTopics((prev) => [
          ...prev,
          { ...profileSubTopicForm, profile_sub_topic_id: data.profile_sub_topic_id, profile_topic: topicName },
        ]);
        setProfileSubTopicForm({});
        setSubmitMsg("Profile Sub Topic saved successfully!");
      } else if (currentStep === 31) {
        const data = await post(`${API_BASE}/master/service-types`, serviceTypeForm);
        setServiceTypes((prev) => [
          ...prev,
          { ...serviceTypeForm, service_type_id: data.service_type_id },
        ]);
        setServiceTypeForm({});
        setSubmitMsg("Service Type saved successfully!");
      } else if (currentStep === 32) {
        const data = await post(`${API_BASE}/master/services`, serviceForm);
        const typeName = serviceTypes.find(
          (st) => String(st.service_type_id) === String(serviceForm.service_type_id)
        )?.service_type;
        setServices((prev) => [
          ...prev,
          { ...serviceForm, service_id: data.service_id, service_type: typeName },
        ]);
        setServiceForm({});
        setSubmitMsg("Service saved successfully!");
      } else if (currentStep === 33) {
        const data = await post(`${API_BASE}/master/question-types`, questionTypeForm);
        setQuestionTypes((prev) => [
          ...prev,
          { ...questionTypeForm, question_type_id: data.question_type_id },
        ]);
        setQuestionTypeForm({});
        setSubmitMsg("Question Type saved successfully!");
      } else if (currentStep === 34) {
        const fd = new FormData();
        appendQuestionFormData(fd, questionForm);
        const r = await fetch(`${API_BASE}/master/questions`, { method: "POST", body: fd });
        const ct = r.headers.get("content-type") || "";
        if (!ct.includes("application/json")) throw new Error("Backend not reachable — is the server running?");
        const data = await r.json();
        if (!r.ok) throw new Error(data.message);
        const listRes = await fetch(`${API_BASE}/master/questions`);
        if (listRes.ok) setQuestions(await listRes.json());
        setQuestionForm({});
        setSubmitMsg("Question saved successfully!");
      } else if (currentStep === 35) {
        const data = await post(`${API_BASE}/master/options`, optionForm);
        const questionText = questions.find(
          (q) => String(q.question_id) === String(optionForm.question_id)
        )?.question_text;
        setOptions((prev) => [
          ...prev,
          { ...optionForm, option_id: data.option_id, question_text: questionText },
        ]);
        setOptionForm({});
        setSubmitMsg("Option saved successfully!");
      } else if (currentStep === 36) {
        const data = await post(`${API_BASE}/master/question-groups`, questionGroupForm);
        const subTopicName = profileSubTopics.find(
          (pst) => String(pst.profile_sub_topic_id) === String(questionGroupForm.profile_sub_topic_id)
        )?.profile_sub_topic;
        const serviceTitle = services.find(
          (s) => String(s.service_id) === String(questionGroupForm.service_id)
        )?.service_title;
        setQuestionGroups((prev) => [
          ...prev,
          {
            ...questionGroupForm,
            question_group_id: data.question_group_id,
            profile_sub_topic: subTopicName,
            service_title: serviceTitle,
          },
        ]);
        setQuestionGroupForm({});
        setSubmitMsg("Question Group saved successfully!");
      } else if (currentStep === 37) {
        const fd = buildEstablishmentFormData(establishmentForm);
        const r = await fetch(`${API_BASE}/master/establishments`, { method: "POST", body: fd });
        const ct = r.headers.get("content-type") || "";
        if (!ct.includes("application/json")) throw new Error("Backend not reachable — is the server running?");
        const data = await r.json();
        if (!r.ok) throw new Error(data.message);
        const refreshed = await fetch(`${API_BASE}/master/establishments`).then((res) => res.json());
        setEstablishments(refreshed);
        setEstablishmentForm({});
        setSubmitMsg("Establishment saved successfully!");
      } else if (currentStep === 38) {
        const fd = buildDoctorFormData(doctorForm);
        const r = await fetch(`${API_BASE}/master/doctors`, { method: "POST", body: fd });
        const ct = r.headers.get("content-type") || "";
        if (!ct.includes("application/json")) throw new Error("Backend not reachable — is the server running?");
        const data = await r.json();
        if (!r.ok) throw new Error(data.message);
        const refreshed = await fetch(`${API_BASE}/master/doctors`).then((res) => res.json());
        setDoctors(refreshed);
        const saved = data.doctor
          || refreshed.find((d) => String(d.doctor_id) === String(data.doctor_id));
        if (saved) {
          setDoctorForm({});
          navigateToDoctorCreateLogin(saved);
          setSubmitMsg("Doctor saved. Complete login details on the Doctor Admin page.");
        } else {
          setDoctorForm({});
          setSubmitMsg("Doctor saved successfully!");
        }
      } else if (currentStep === 39) {
        const data = await post(`${API_BASE}/master/event-types`, eventTypeForm);
        setEventTypes((prev) => [
          ...prev,
          { ...eventTypeForm, event_type_id: data.event_type_id },
        ]);
        setEventTypeForm({});
        setSubmitMsg("Event Type saved successfully!");
      } else if (currentStep === 41) {
        const data = await post(`${API_BASE}/master/immunizations`, immunizationForm);
        const morbidityName = morbidityDiseases.find(
          (d) => String(d.disease_id) === String(immunizationForm.applicable_morbidity_id)
        )?.disease_name;
        setImmunizations((prev) => [
          ...prev,
          { ...immunizationForm, immunization_id: data.immunization_id, applicable_morbidity: morbidityName },
        ]);
        setImmunizationForm({});
        setSubmitMsg("Immunization saved successfully!");
      } else if (currentStep === 42) {
        const data = await post(`${API_BASE}/master/vaccines`, vaccineForm);
        setVaccines((prev) => [
          ...prev,
          { ...vaccineForm, vaccine_id: data.vaccine_id },
        ]);
        setVaccineForm({});
        setSubmitMsg("Vaccine saved successfully!");
      } else if (currentStep === 46) {
        const data = await post(`${API_BASE}/master/relationships`, relationshipForm);
        setRelationships((prev) => [
          ...prev,
          { ...relationshipForm, relationship_id: data.relationship_id },
        ]);
        setRelationshipForm({});
        setSubmitMsg("Relationship saved successfully!");
      } else if (currentStep === 47) {
        const data = await post(`${API_BASE}/master/blood-groups`, bloodGroupForm);
        setBloodGroups((prev) => [
          ...prev,
          { ...bloodGroupForm, blood_group_id: data.blood_group_id },
        ]);
        setBloodGroupForm({});
        setSubmitMsg("Blood group saved successfully!");
      } else if (currentStep === 48) {
        const fd = buildPatientFormData(patientForm);
        if (editingPatientId) {
          const r = await fetch(`${API_BASE}/master/patients/${editingPatientId}`, {
            method: "PUT",
            body: fd,
          });
          if (!r.ok) throw new Error(await parseApiError(r));
          const refreshed = await fetch(`${API_BASE}/master/patients`).then((res) => res.json());
          setPatients(refreshed);
          setEditingPatientId(null);
          setPatientForm(emptyPatientForm());
          setSubmitMsg("Patient updated successfully!");
        } else {
          const r = await fetch(`${API_BASE}/master/patients`, { method: "POST", body: fd });
          const ct = r.headers.get("content-type") || "";
          if (!ct.includes("application/json")) throw new Error("Backend not reachable — is the server running?");
          const data = await r.json();
          if (!r.ok) throw new Error(data.message);
          const refreshed = await fetch(`${API_BASE}/master/patients`).then((res) => res.json());
          setPatients(refreshed);
          setPatientForm(emptyPatientForm());
          setSubmitMsg("Patient saved successfully!");
        }
      } else if (currentStep === 68) {
        const fd = buildUserFormData(userForm);
        if (editingUserId) {
          const r = await fetch(`${API_BASE}/master/users/${editingUserId}`, { method: "PUT", body: fd });
          if (!r.ok) throw new Error(await parseApiError(r));
          const refreshed = await fetch(`${API_BASE}/master/users`).then((res) => res.json());
          setUsers(refreshed);
          setEditingUserId(null);
          setUserForm(emptyUserForm());
          setSubmitMsg("User updated successfully!");
        } else {
          const r = await fetch(`${API_BASE}/master/users`, { method: "POST", body: fd });
          const ct = r.headers.get("content-type") || "";
          if (!ct.includes("application/json")) throw new Error("Backend not reachable — is the server running?");
          const data = await r.json();
          if (!r.ok) throw new Error(data.message);
          const refreshed = await fetch(`${API_BASE}/master/users`).then((res) => res.json());
          setUsers(refreshed);
          setUserForm(emptyUserForm());
          setSubmitMsg("User saved successfully!");
        }
      } else if (currentStep === 49) {
        const data = await post(`${API_BASE}/master/therapeutic-classes`, therapeuticClassForm);
        setTherapeuticClasses((prev) => [
          ...prev,
          { ...therapeuticClassForm, therapeutic_class_id: data.therapeutic_class_id },
        ]);
        setTherapeuticClassForm({});
        setSubmitMsg("Therapeutic class saved successfully!");
      } else if (currentStep === 50) {
        await post(`${API_BASE}/master/medicine-salts`, medicineSaltForm);
        const listRes = await fetch(`${API_BASE}/master/medicine-salts`);
        if (listRes.ok) setMedicineSalts(await listRes.json());
        setMedicineSaltForm({});
        setSubmitMsg("Medicine salt saved successfully!");
      } else if (currentStep === 51) {
        const fd = new FormData();
        appendPrescriptionFormData(fd, medicalPrescriptionForm);
        const r = await fetch(`${API_BASE}/master/medical-prescriptions`, { method: "POST", body: fd });
        const ct = r.headers.get("content-type") || "";
        if (!ct.includes("application/json")) throw new Error("Backend not reachable — is the server running?");
        const data = await r.json();
        if (!r.ok) throw new Error(data.message);
        const listRes = await fetch(`${API_BASE}/master/medical-prescriptions`);
        if (listRes.ok) setMedicalPrescriptions(await listRes.json());
        setMedicalPrescriptionForm({});
        setSubmitMsg("Medical prescription saved successfully!");
      } else if (currentStep === 52) {
        await post(`${API_BASE}/master/dosages`, medicineDosageForm);
        const listRes = await fetch(`${API_BASE}/master/dosages`);
        if (listRes.ok) setMedicineDosages(await listRes.json());
        setMedicineDosageForm({});
        setSubmitMsg("Dosage type saved successfully!");
      } else if (currentStep === 53) {
        await post(
          `${API_BASE}/master/patient-prescription-medicines`,
          buildPatientPrescriptionMedicinePayload(patientPrescriptionMedicineForm)
        );
        const listRes = await fetch(`${API_BASE}/master/patient-prescription-medicines`);
        if (listRes.ok) setPatientPrescriptionMedicines(await listRes.json());
        setPatientPrescriptionMedicineForm({ is_empty_stomach: 0, is_sos: 0 });
        setSubmitMsg("Patient prescription medicine saved successfully!");
      } else if (currentStep === 54) {
        await post(`${API_BASE}/master/patient-cases`, buildPatientCasePayload(patientCaseForm));
        const listRes = await fetch(`${API_BASE}/master/patient-cases`);
        if (listRes.ok) setPatientCases(await listRes.json());
        setPatientCaseForm({});
        setSubmitMsg("Patient case saved successfully!");
      } else if (currentStep === 55) {
        const caseId = patientComplaintForm.case_id;
        await post(
          `${API_BASE}/master/patient-cases/${caseId}/complaints`,
          buildPatientComplaintPayload(patientComplaintForm)
        );
        const listRes = await fetch(`${API_BASE}/master/patient-complaints`);
        if (listRes.ok) setPatientComplaints(await listRes.json());
        setPatientComplaintForm({ is_sos: false, aggravating_factor_ids: [], aggravating_factors: [] });
        setSubmitMsg("Patient complaint saved successfully!");
      } else if (currentStep === 56) {
        const caseId = caseDiagnosisForm.case_id;
        await post(
          `${API_BASE}/master/patient-cases/${caseId}/diagnoses`,
          buildCaseDiagnosisPayload(caseDiagnosisForm)
        );
        const listRes = await fetch(`${API_BASE}/master/case-diagnoses`);
        if (listRes.ok) setCaseDiagnoses(await listRes.json());
        setCaseDiagnosisForm({ status: "Provisional" });
        setSubmitMsg("Case diagnosis saved successfully!");
      } else if (currentStep === 57) {
        const caseId = patientInvestigationForm.case_id;
        const fd = new FormData();
        appendInvestigationFormData(fd, patientInvestigationForm);
        const r = await fetch(`${API_BASE}/master/patient-cases/${caseId}/investigations`, { method: "POST", body: fd });
        const ct = r.headers.get("content-type") || "";
        if (!ct.includes("application/json")) throw new Error("Backend not reachable — is the server running?");
        const data = await r.json();
        if (!r.ok) throw new Error(data.message);
        const listRes = await fetch(`${API_BASE}/master/patient-investigations`);
        if (listRes.ok) setPatientInvestigations(await listRes.json());
        setPatientInvestigationForm({});
        setSubmitMsg("Patient investigation saved successfully!");
      } else if (currentStep === 58) {
        const data = await post(`${API_BASE}/master/supplier-types`, supplierTypeForm);
        setSupplierTypes((prev) => [...prev, { ...supplierTypeForm, supplier_type_id: data.supplier_type_id }]);
        setSupplierTypeForm({});
        setSubmitMsg("Supplier Type saved successfully!");
      } else if (currentStep === 59) {
        const payload = {
          ...productCategoryForm,
          status: productCategoryForm.status || "Active",
        };
        const data = await post(`${API_BASE}/master/product-categories`, payload);
        setProductCategories((prev) => [...prev, { ...payload, product_category_id: data.product_category_id }]);
        setProductCategoryForm({ status: "Active" });
        setSubmitMsg("Product Category saved successfully!");
      } else if (currentStep === 60) {
        const payload = {
          ...productSubCategoryForm,
          status: productSubCategoryForm.status || "Active",
        };
        const data = await post(`${API_BASE}/master/product-sub-categories`, payload);
        const matchedCategory = productCategories.find(
          (pc) => String(pc.product_category_id) === String(payload.product_category_id)
        );
        setProductSubCategories((prev) => [
          ...prev,
          {
            ...payload,
            product_sub_category_id: data.product_sub_category_id,
            product_category: matchedCategory?.product_category,
            category_code: matchedCategory?.category_code,
            category_group: matchedCategory?.category_group,
          },
        ]);
        setProductSubCategoryForm({ status: "Active" });
        setSubmitMsg("Product Sub Category saved successfully!");
      } else if (currentStep === 61) {
        const fd = new FormData();
        appendProductFormData(fd, productForm);
        const r = await fetch(`${API_BASE}/master/products`, { method: "POST", body: fd });
        const ct = r.headers.get("content-type") || "";
        if (!ct.includes("application/json")) throw new Error("Backend not reachable — is the server running?");
        const data = await r.json();
        if (!r.ok) throw new Error(data.message);
        const listRes = await fetch(`${API_BASE}/master/products`);
        if (listRes.ok) setProducts(await listRes.json());
        setProductForm({ status: "Active" });
        setSubmitMsg("Product saved successfully!");
      } else if (currentStep === 62) {
        const data = await post(`${API_BASE}/master/medical-services`, medicalServiceForm);
        setMedicalServices((prev) => [
          ...prev,
          { ...medicalServiceForm, medical_service_id: data.medical_service_id },
        ]);
        setMedicalServiceForm({});
        setSubmitMsg("Medical Service saved successfully!");
      } else if (currentStep === 70) {
        const data = await post(`${API_BASE}/master/mvt-journey-phases`, mvtJourneyPhaseForm);
        setMvtJourneyPhases((prev) => [...prev, { ...mvtJourneyPhaseForm, mvt_journey_phase_id: data.mvt_journey_phase_id }]);
        setMvtJourneyPhaseForm({});
        setSubmitMsg("MVT Journey Phase saved successfully!");
      } else if (currentStep === 71) {
        const data = await post(`${API_BASE}/master/mvt-journey-steps`, mvtJourneyStepForm);
        const phaseName = mvtJourneyPhases.find(
          (p) => String(p.mvt_journey_phase_id) === String(mvtJourneyStepForm.mvt_journey_phase_id)
        )?.mvt_journey_phase;
        setMvtJourneySteps((prev) => [
          ...prev,
          { ...mvtJourneyStepForm, mvt_journey_step_id: data.mvt_journey_step_id, mvt_journey_phase: phaseName },
        ]);
        setMvtJourneyStepForm({});
        setSubmitMsg("MVT Journey Step saved successfully!");
      } else {
        setSubmitMsg("Submitted successfully!");
      }
      setValidated(false);
    } catch (e) {
      setSubmitMsg(`Error: ${e.message}`);
    }
  };

  const renderForm = () => {
    if (currentStep === 0)
      return <UserTypeMasterForm formData={userTypeForm} onChange={(f, v) => setUserTypeForm((p) => ({ ...p, [f]: v }))} />;
    if (currentStep === 1)
      return <LocationTypeMasterForm formData={locationTypeForm} onChange={handleLocationTypeChange} />;
    if (currentStep === 2)
      return <LocationMasterForm formData={locationForm} onChange={handleLocationChange} locationTypes={locationTypes} locations={locations} />;
    if (currentStep === 3)
      return (
        <MedicalSpecialtyForm
          formData={specialtyForm}
          onChange={(f, v) => setSpecialtyForm((p) => ({ ...p, [f]: v }))}
        />
      );
    if (currentStep === 4)
      return (
        <MedicalProcedureForm
          formData={medicalProcedureForm}
          onChange={(f, v) => setMedicalProcedureForm((p) => ({ ...p, [f]: v }))}
          specialties={specialties}
        />
      );
    if (currentStep === 5)
      return (
        <DiseaseForm
          formData={diseaseForm}
          onChange={(f, v) => setDiseaseForm((p) => ({ ...p, [f]: v }))}
          specialties={specialties}
        />
      );
    if (currentStep === 6)
      return (
        <SymptomCategoryForm
          formData={symptomCategoryForm}
          onChange={(f, v) => setSymptomCategoryForm((p) => ({ ...p, [f]: v }))}
        />
      );
    if (currentStep === 7)
      return (
        <SymptomForm
          formData={symptomForm}
          onChange={(f, v) => setSymptomForm((p) => ({ ...p, [f]: v }))}
          symptomCategories={symptomCategories}
        />
      );
    if (currentStep === 8)
      return (
        <AggravatingFactorForm
          formData={aggravatingFactorForm}
          onChange={(f, v) => setAggravatingFactorForm((p) => ({ ...p, [f]: v }))}
        />
      );
    if (currentStep === 9)
      return (
        <AllergyTypeForm
          formData={allergyTypeForm}
          onChange={(f, v) => setAllergyTypeForm((p) => ({ ...p, [f]: v }))}
        />
      );
    if (currentStep === 10)
      return (
        <AllergyForm
          formData={allergyForm}
          onChange={(f, v) => setAllergyForm((p) => ({ ...p, [f]: v }))}
          allergyTypes={allergyTypes}
        />
      );
    if (currentStep === 11)
      return (
        <TraumaTypeForm
          formData={traumaTypeForm}
          onChange={(f, v) => setTraumaTypeForm((p) => ({ ...p, [f]: v }))}
        />
      );
    if (currentStep === 12)
      return <TraumaForm formData={traumaForm} onChange={(f, v) => setTraumaForm((p) => ({ ...p, [f]: v }))} traumaTypes={traumaTypes} />;
    if (currentStep === 13)
      return <AddictionCategoryForm formData={addictionCategoryForm} onChange={(f, v) => setAddictionCategoryForm((p) => ({ ...p, [f]: v }))} />;
    if (currentStep === 14)
      return (
        <AddictionSubCategoryForm
          formData={addictionSubCategoryForm}
          onChange={(f, v) => setAddictionSubCategoryForm((p) => ({ ...p, [f]: v }))}
          addictionCategories={addictionCategories}
        />
      );
    if (currentStep === 15)
      return (
        <AddictionsForm
          formData={addictionsForm}
          onChange={(f, v) => {
            if (f === "addiction_category_id") {
              setAddictionsForm((p) => ({ ...p, addiction_category_id: v, addiction_sub_category_id: "" }));
            } else {
              setAddictionsForm((p) => ({ ...p, [f]: v }));
            }
          }}
          addictionCategories={addictionCategories}
          addictionSubCategories={addictionSubCategories}
        />
      );
    if (currentStep === 16)
      return <PatientConditionForm formData={patientConditionForm} onChange={(f, v) => setPatientConditionForm((p) => ({ ...p, [f]: v }))} />;
    if (currentStep === 17)
      return <InvestigationTypeForm formData={investigationTypeForm} onChange={(f, v) => setInvestigationTypeForm((p) => ({ ...p, [f]: v }))} />;
    if (currentStep === 18)
      return <InvestigationGroupForm formData={investigationGroupForm} onChange={(f, v) => setInvestigationGroupForm((p) => ({ ...p, [f]: v }))} investigationTypes={investigationTypes} />;
    if (currentStep === 19)
      return (
        <InvestigationForm
          formData={investigationForm}
          onChange={(f, v) => setInvestigationForm((p) => ({ ...p, [f]: v }))}
          investigationTypes={investigationTypes}
          investigationGroups={investigationGroups}
          specialties={specialties}
        />
      );
    if (currentStep === 20)
      return <EstablishmentTypeForm formData={estTypeForm} onChange={(f, v) => setEstTypeForm((p) => ({ ...p, [f]: v }))} />;
    if (currentStep === 21)
      return (
        <EstablishmentSubTypeForm
          formData={establishmentSubTypeForm}
          onChange={(f, v) => setEstablishmentSubTypeForm((p) => ({ ...p, [f]: v }))}
          estTypes={estTypes}
        />
      );
    if (currentStep === 22)
      return <MedicalDegreeForm formData={degreeForm} onChange={(f, v) => setDegreeForm((p) => ({ ...p, [f]: v }))} />;
    if (currentStep === 23)
      return (
        <SpecializationForm
          formData={specializationForm}
          onChange={(f, v) => setSpecializationForm((p) => ({ ...p, [f]: v }))}
          specializationTypes={specializationTypes}
        />
      );
    if (currentStep === 24)
      return <SpecializationTypeForm formData={specializationTypeForm} onChange={(f, v) => setSpecializationTypeForm((p) => ({ ...p, [f]: v }))} />;
    if (currentStep === 25)
      return <AccreditationForm formData={accreditationForm} onChange={(f, v) => setAccreditationForm((p) => ({ ...p, [f]: v }))} />;
    if (currentStep === 26)
      return <DepartmentCategoryForm formData={departmentCategoryForm} onChange={(f, v) => setDepartmentCategoryForm((p) => ({ ...p, [f]: v }))} />;
    if (currentStep === 27)
      return (
        <DepartmentMasterForm
          formData={departmentForm}
          onChange={(f, v) => setDepartmentForm((p) => ({ ...p, [f]: v }))}
          departmentCategories={departmentCategories}
        />
      );
    if (currentStep === 28)
      return <DesignationForm formData={designationForm} onChange={(f, v) => setDesignationForm((p) => ({ ...p, [f]: v }))} />;
    if (currentStep === 29)
      return (
        <ProfileTopicForm
          formData={profileTopicForm}
          onChange={(f, v) => setProfileTopicForm((p) => ({ ...p, [f]: v }))}
        />
      );
    if (currentStep === 30)
      return (
        <ProfileSubTopicForm
          formData={profileSubTopicForm}
          onChange={(f, v) => setProfileSubTopicForm((p) => ({ ...p, [f]: v }))}
          profileTopics={profileTopics}
        />
      );
    if (currentStep === 31)
      return (
        <ServiceTypeForm
          formData={serviceTypeForm}
          onChange={(f, v) => setServiceTypeForm((p) => ({ ...p, [f]: v }))}
        />
      );
    if (currentStep === 32)
      return (
        <ServiceForm
          formData={serviceForm}
          onChange={(f, v) => setServiceForm((p) => ({ ...p, [f]: v }))}
          serviceTypes={serviceTypes}
        />
      );
    if (currentStep === 33)
      return (
        <QuestionTypeForm
          formData={questionTypeForm}
          onChange={(f, v) => setQuestionTypeForm((p) => ({ ...p, [f]: v }))}
        />
      );
    if (currentStep === 34)
      return (
        <QuestionForm
          formData={questionForm}
          onChange={handleQuestionFormChange}
          questionTypes={questionTypes}
          profileTopics={profileTopics}
          profileSubTopics={profileSubTopics}
          services={services}
        />
      );
    if (currentStep === 35)
      return (
        <OptionForm
          formData={optionForm}
          onChange={(f, v) => setOptionForm((p) => ({ ...p, [f]: v }))}
          questions={questions}
        />
      );
    if (currentStep === 36)
      return (
        <QuestionGroupForm
          formData={questionGroupForm}
          onChange={(f, v) => setQuestionGroupForm((p) => ({ ...p, [f]: v }))}
          profileSubTopics={profileSubTopics}
          services={services}
        />
      );
    if (currentStep === 37)
      return (
        <EstablishmentProfileForm
          basicOnly
          formData={establishmentForm}
          onChange={(f, v) => setEstablishmentForm((p) => {
            const next = { ...p, [f]: v };
            if (f === "establishment_type_id") {
              next.establishment_sub_type_id = "";
            }
            return next;
          })}
          establishments={establishments}
          estTypes={estTypes}
          establishmentSubTypes={establishmentSubTypes}
          locationTypes={locationTypes}
          locations={locations}
          accreditations={accreditations}
          departments={departments}
          specializationTypes={specializationTypes}
        />
      );
    if (currentStep === 38)
      return (
        <DoctorProfileForm
          formMode="admin"
          formData={doctorForm}
          onChange={(f, v) => setDoctorForm((p) => ({ ...p, [f]: v }))}
          userTypes={userTypes}
          establishments={establishments}
          locationTypes={locationTypes}
          locations={locations}
          specialties={specialties}
          medicalProcedures={medicalProcedures}
          specializations={specializations}
          departments={departments}
          designations={designations}
          degrees={degrees}
          accreditations={accreditations}
        />
      );
    if (currentStep === 39)
      return (
        <EventTypeForm
          formData={eventTypeForm}
          onChange={(f, v) => setEventTypeForm((p) => ({ ...p, [f]: v }))}
        />
      );
    if (currentStep === 41)
      return (
        <ImmunizationForm
          formData={immunizationForm}
          onChange={(f, v) => setImmunizationForm((p) => ({ ...p, [f]: v }))}
          morbidityDiseases={morbidityDiseases}
        />
      );
    if (currentStep === 42)
      return (
        <VaccineForm
          formData={vaccineForm}
          onChange={(f, v) => setVaccineForm((p) => ({ ...p, [f]: v }))}
        />
      );
    if (currentStep === 46)
      return <RelationshipForm formData={relationshipForm} onChange={(f, v) => setRelationshipForm((p) => ({ ...p, [f]: v }))} />;
    if (currentStep === 47)
      return <BloodGroupForm formData={bloodGroupForm} onChange={(f, v) => setBloodGroupForm((p) => ({ ...p, [f]: v }))} />;
    if (currentStep === 48)
      return (
        <PatientMasterForm
          formData={patientForm}
          onChange={(f, v) => setPatientForm((p) => ({ ...p, [f]: v }))}
          doctors={doctors}
          bloodGroups={bloodGroups}
          relationships={relationships}
          locationTypes={locationTypes}
          locations={locations}
        />
      );
    if (currentStep === 68)
      return (
        <UserMasterForm
          formData={userForm}
          onChange={(f, v) => setUserForm((p) => ({ ...p, [f]: v }))}
          userTypes={userTypes}
          establishments={establishments}
          locationTypes={locationTypes}
          locations={locations}
        />
      );
    if (currentStep === 49)
      return (
        <TherapeuticClassForm
          formData={therapeuticClassForm}
          onChange={(f, v) => setTherapeuticClassForm((p) => ({ ...p, [f]: v }))}
        />
      );
    if (currentStep === 50)
      return (
        <MedicineSaltForm
          formData={medicineSaltForm}
          onChange={(f, v) => setMedicineSaltForm((p) => ({ ...p, [f]: v }))}
          therapeuticClasses={therapeuticClasses}
        />
      );
    if (currentStep === 51)
      return (
        <MedicalPrescriptionForm
          formData={medicalPrescriptionForm}
          onChange={handleMedicalPrescriptionChange}
          patients={patients}
          patientCases={patientCases}
          doctors={doctors}
          establishments={establishments}
        />
      );
    if (currentStep === 52)
      return (
        <DosageForm
          formData={medicineDosageForm}
          onChange={(f, v) => setMedicineDosageForm((p) => ({ ...p, [f]: v }))}
        />
      );
    if (currentStep === 53)
      return (
        <PatientPrescriptionMedicineForm
          formData={patientPrescriptionMedicineForm}
          onChange={handlePatientPrescriptionMedicineChange}
          patients={patients}
          patientCases={patientCases}
          medicalPrescriptions={medicalPrescriptions}
          medicineSalts={medicineSalts}
          medicineDosages={medicineDosages}
        />
      );
    if (currentStep === 54)
      return (
        <PatientCaseForm
          formData={patientCaseForm}
          onChange={handlePatientCaseChange}
          patients={patients}
          doctors={doctors}
          specialties={specialties}
          medicalServices={medicalServices}
        />
      );
    if (currentStep === 55)
      return (
        <PatientComplaintForm
          formData={patientComplaintForm}
          onChange={handlePatientComplaintChange}
          patients={patients}
          patientCases={patientCases}
          symptoms={symptoms}
          aggravatingFactors={aggravatingFactors}
        />
      );
    if (currentStep === 56)
      return (
        <CaseDiagnosisForm
          formData={caseDiagnosisForm}
          onChange={handleCaseDiagnosisChange}
          patients={patients}
          patientCases={patientCases}
          diseases={diseases}
        />
      );
    if (currentStep === 57)
      return (
        <PatientInvestigationForm
          formData={patientInvestigationForm}
          onChange={handlePatientInvestigationChange}
          patients={patients}
          patientCases={patientCases}
          caseDiagnoses={caseDiagnoses}
          investigations={investigations}
        />
      );
    if (currentStep === 58)
      return <SupplierTypeForm formData={supplierTypeForm} onChange={(f, v) => setSupplierTypeForm((p) => ({ ...p, [f]: v }))} />;
    if (currentStep === 59)
      return <ProductCategoryForm formData={productCategoryForm} onChange={(f, v) => setProductCategoryForm((p) => ({ ...p, [f]: v }))} />;
    if (currentStep === 60)
      return (
        <ProductSubCategoryForm
          formData={productSubCategoryForm}
          onChange={(f, v) => setProductSubCategoryForm((p) => ({ ...p, [f]: v }))}
          productCategories={productCategories}
        />
      );
    if (currentStep === 61)
      return (
        <ProductMasterForm
          formData={productForm}
          onChange={handleProductFormChange}
          productCategories={productCategories}
          productSubCategories={productSubCategories}
          specialties={specialties}
        />
      );
    if (currentStep === 62)
      return (
        <MedicalServiceForm
          formData={medicalServiceForm}
          onChange={(f, v) => setMedicalServiceForm((p) => ({ ...p, [f]: v }))}
        />
      );
    if (currentStep === 70)
      return <MvtJourneyPhaseForm formData={mvtJourneyPhaseForm} onChange={(f, v) => setMvtJourneyPhaseForm((p) => ({ ...p, [f]: v }))} />;
    if (currentStep === 71)
      return (
        <MvtJourneyStepForm
          formData={mvtJourneyStepForm}
          onChange={(f, v) => setMvtJourneyStepForm((p) => ({ ...p, [f]: v }))}
          mvtJourneyPhases={mvtJourneyPhases}
        />
      );
    if (currentStep === 40) return null;
    if (currentStep === 43) return null;
    if (currentStep === 44) return null;
    if (isLoginAdminStep(currentStep)) return null;
    return <AccreditationForm formData={accreditationForm} onChange={(f, v) => setAccreditationForm((p) => ({ ...p, [f]: v }))} />;
  };

  const toggleMenuGroup = (groupId) => {
    const isOpen = !!expandedGroups[groupId];
    if (isOpen) {
      setExpandedGroups({});
      setExpandedSections((prev) =>
        Object.fromEntries(
          Object.entries(prev).filter(([key]) => !key.startsWith(`${groupId}:`))
        )
      );
    } else {
      setExpandedGroups({ [groupId]: true });
      setExpandedSections({});
    }
  };

  const toggleMenuSection = (groupId, sectionId) => {
    const key = `${groupId}:${sectionId}`;
    setExpandedSections((prev) => {
      const isOpen = !!prev[key];
      if (isOpen) {
        const next = { ...prev };
        delete next[key];
        return next;
      }
      return { [key]: true };
    });
  };

  const selectStep = (stepIndex) => {
    setCurrentStep(stepIndex);
    setSidebarOpen(false);
    const parentGroup = MENU_GROUPS.find((g) => getGroupSteps(g).includes(stepIndex));
    if (parentGroup) {
      setExpandedGroups({ [parentGroup.id]: true });
      if (parentGroup.sections) {
        const activeSection = parentGroup.sections.find((section) => {
          if (section.step === stepIndex) return true;
          return section.items?.some((item) => item.step === stepIndex);
        });
        if (activeSection) {
          setExpandedSections({ [`${parentGroup.id}:${activeSection.id}`]: true });
        } else {
          setExpandedSections({});
        }
      }
    }
  };

  return {
    currentStep,
    setCurrentStep,
    validated,
    setValidated,
    userTypeForm,
    setUserTypeForm,
    userTypes,
    setUserTypes,
    locationTypes,
    setLocationTypes,
    locations,
    setLocations,
    locationTypeForm,
    setLocationTypeForm,
    locationForm,
    setLocationForm,
    specialtyForm,
    setSpecialtyForm,
    estTypeForm,
    setEstTypeForm,
    specialties,
    setSpecialties,
    medicalProcedureForm,
    setMedicalProcedureForm,
    medicalProcedures,
    setMedicalProcedures,
    diseaseForm,
    setDiseaseForm,
    diseases,
    setDiseases,
    symptomCategoryForm,
    setSymptomCategoryForm,
    symptomCategories,
    setSymptomCategories,
    symptomForm,
    setSymptomForm,
    symptoms,
    setSymptoms,
    aggravatingFactorForm,
    setAggravatingFactorForm,
    aggravatingFactors,
    setAggravatingFactors,
    allergyTypeForm,
    setAllergyTypeForm,
    allergyTypes,
    setAllergyTypes,
    allergyForm,
    setAllergyForm,
    allergies,
    setAllergies,
    traumaTypeForm,
    setTraumaTypeForm,
    traumaTypes,
    setTraumaTypes,
    traumaForm,
    setTraumaForm,
    traumas,
    setTraumas,
    addictionCategoryForm,
    setAddictionCategoryForm,
    addictionCategories,
    setAddictionCategories,
    addictionSubCategoryForm,
    setAddictionSubCategoryForm,
    addictionSubCategories,
    setAddictionSubCategories,
    addictionsForm,
    setAddictionsForm,
    addictionsList,
    setAddictionsList,
    patientConditionForm,
    setPatientConditionForm,
    patientConditions,
    setPatientConditions,
    investigationTypeForm,
    setInvestigationTypeForm,
    investigationTypes,
    setInvestigationTypes,
    investigationGroupForm,
    setInvestigationGroupForm,
    investigationGroups,
    setInvestigationGroups,
    investigationForm,
    setInvestigationForm,
    investigations,
    setInvestigations,
    estTypes,
    setEstTypes,
    establishmentSubTypeForm,
    setEstablishmentSubTypeForm,
    establishmentSubTypes,
    setEstablishmentSubTypes,
    degreeForm,
    setDegreeForm,
    specializationForm,
    setSpecializationForm,
    degrees,
    setDegrees,
    specializations,
    setSpecializations,
    specializationTypeForm,
    setSpecializationTypeForm,
    specializationTypes,
    setSpecializationTypes,
    accreditationForm,
    setAccreditationForm,
    accreditations,
    setAccreditations,
    departmentCategoryForm,
    setDepartmentCategoryForm,
    departmentCategories,
    setDepartmentCategories,
    departmentForm,
    setDepartmentForm,
    departments,
    setDepartments,
    designationForm,
    setDesignationForm,
    designations,
    setDesignations,
    relationshipForm,
    setRelationshipForm,
    relationships,
    setRelationships,
    bloodGroupForm,
    setBloodGroupForm,
    bloodGroups,
    setBloodGroups,
    therapeuticClassForm,
    setTherapeuticClassForm,
    therapeuticClasses,
    setTherapeuticClasses,
    medicineSaltForm,
    setMedicineSaltForm,
    medicineSalts,
    setMedicineSalts,
    medicalPrescriptionForm,
    setMedicalPrescriptionForm,
    medicalPrescriptions,
    setMedicalPrescriptions,
    medicineDosageForm,
    setMedicineDosageForm,
    medicineDosages,
    setMedicineDosages,
    patientPrescriptionMedicineForm,
    setPatientPrescriptionMedicineForm,
    patientPrescriptionMedicines,
    setPatientPrescriptionMedicines,
    patientCases,
    setPatientCases,
    patientCaseForm,
    setPatientCaseForm,
    patientComplaints,
    setPatientComplaints,
    patientComplaintForm,
    setPatientComplaintForm,
    caseDiagnoses,
    setCaseDiagnoses,
    caseDiagnosisForm,
    setCaseDiagnosisForm,
    patientInvestigations,
    setPatientInvestigations,
    patientInvestigationForm,
    setPatientInvestigationForm,
    supplierTypeForm,
    setSupplierTypeForm,
    supplierTypes,
    setSupplierTypes,
    productCategoryForm,
    setProductCategoryForm,
    productCategories,
    setProductCategories,
    productSubCategoryForm,
    setProductSubCategoryForm,
    productSubCategories,
    setProductSubCategories,
    productForm,
    setProductForm,
    products,
    setProducts,
    mvtJourneyPhaseForm,
    setMvtJourneyPhaseForm,
    mvtJourneyPhases,
    setMvtJourneyPhases,
    mvtJourneyStepForm,
    setMvtJourneyStepForm,
    mvtJourneySteps,
    setMvtJourneySteps,
    profileTopicForm,
    setProfileTopicForm,
    profileTopics,
    setProfileTopics,
    profileSubTopicForm,
    setProfileSubTopicForm,
    profileSubTopics,
    setProfileSubTopics,
    serviceTypeForm,
    setServiceTypeForm,
    serviceTypes,
    setServiceTypes,
    medicalServiceForm,
    setMedicalServiceForm,
    medicalServices,
    setMedicalServices,
    serviceForm,
    setServiceForm,
    services,
    setServices,
    questionTypeForm,
    setQuestionTypeForm,
    questionTypes,
    setQuestionTypes,
    questionForm,
    setQuestionForm,
    questions,
    setQuestions,
    optionForm,
    setOptionForm,
    options,
    setOptions,
    questionGroupForm,
    setQuestionGroupForm,
    questionGroups,
    setQuestionGroups,
    establishmentForm,
    setEstablishmentForm,
    establishments,
    setEstablishments,
    doctorForm,
    setDoctorForm,
    doctors,
    setDoctors,
    patientForm,
    setPatientForm,
    patients,
    setPatients,
    editingPatientId,
    setEditingPatientId,
    userForm,
    setUserForm,
    users,
    setUsers,
    editingUserId,
    setEditingUserId,
    editingDoctorId,
    setEditingDoctorId,
    editingDoctorData,
    setEditingDoctorData,
    eventTypeForm,
    setEventTypeForm,
    eventTypes,
    setEventTypes,
    immunizationForm,
    setImmunizationForm,
    immunizations,
    setImmunizations,
    vaccineForm,
    setVaccineForm,
    vaccines,
    setVaccines,
    loginLogs,
    setLoginLogs,
    editingEstbId,
    setEditingEstbId,
    editingEstbData,
    setEditingEstbData,
    submitMsg,
    setSubmitMsg,
    editingUserTypeId,
    setEditingUserTypeId,
    editingUserTypeValue,
    setEditingUserTypeValue,
    editingTypeId,
    setEditingTypeId,
    editingTypeValue,
    setEditingTypeValue,
    editingLocId,
    setEditingLocId,
    editingLocData,
    setEditingLocData,
    editingSpecId,
    setEditingSpecId,
    editingSpecData,
    setEditingSpecData,
    editingProcId,
    setEditingProcId,
    editingProcData,
    setEditingProcData,
    editingDiseaseId,
    setEditingDiseaseId,
    editingDiseaseData,
    setEditingDiseaseData,
    editingSymptomId,
    setEditingSymptomId,
    editingSymptomData,
    setEditingSymptomData,
    editingAggravatingFactorId,
    setEditingAggravatingFactorId,
    editingAggravatingFactorValue,
    setEditingAggravatingFactorValue,
    editingAllergyTypeId,
    setEditingAllergyTypeId,
    editingAllergyTypeValue,
    setEditingAllergyTypeValue,
    editingAllergyId,
    setEditingAllergyId,
    editingAllergyData,
    setEditingAllergyData,
    editingTraumaTypeId,
    setEditingTraumaTypeId,
    editingTraumaTypeValue,
    setEditingTraumaTypeValue,
    editingTraumaId,
    setEditingTraumaId,
    editingTraumaData,
    setEditingTraumaData,
    editingAddictionCategoryId,
    setEditingAddictionCategoryId,
    editingAddictionCategoryValue,
    setEditingAddictionCategoryValue,
    editingAddictionSubCategoryId,
    setEditingAddictionSubCategoryId,
    editingAddictionSubCategoryData,
    setEditingAddictionSubCategoryData,
    editingAddictionId,
    setEditingAddictionId,
    editingAddictionData,
    setEditingAddictionData,
    editingPatientConditionId,
    setEditingPatientConditionId,
    editingPatientConditionValue,
    setEditingPatientConditionValue,
    editingInvestigationTypeId,
    setEditingInvestigationTypeId,
    editingInvestigationTypeValue,
    setEditingInvestigationTypeValue,
    editingInvestigationGroupId,
    setEditingInvestigationGroupId,
    editingInvestigationGroupData,
    setEditingInvestigationGroupData,
    editingInvestigationId,
    setEditingInvestigationId,
    editingInvestigationData,
    setEditingInvestigationData,
    editingEstId,
    setEditingEstId,
    editingEstValue,
    setEditingEstValue,
    editingEstSubTypeId,
    setEditingEstSubTypeId,
    editingEstSubTypeData,
    setEditingEstSubTypeData,
    editingDegId,
    setEditingDegId,
    editingDegValue,
    setEditingDegValue,
    editingSpeczId,
    setEditingSpeczId,
    editingSpeczData,
    setEditingSpeczData,
    editingSpecTypeId,
    setEditingSpecTypeId,
    editingSpecTypeValue,
    setEditingSpecTypeValue,
    editingAccrId,
    setEditingAccrId,
    editingAccrValue,
    setEditingAccrValue,
    editingDeptCatId,
    setEditingDeptCatId,
    editingDeptCatValue,
    setEditingDeptCatValue,
    editingDeptId,
    setEditingDeptId,
    editingDeptData,
    setEditingDeptData,
    editingDesigId,
    setEditingDesigId,
    editingDesigValue,
    setEditingDesigValue,
    editingRelationshipId,
    setEditingRelationshipId,
    editingRelationshipValue,
    setEditingRelationshipValue,
    editingBloodGroupId,
    setEditingBloodGroupId,
    editingBloodGroupValue,
    setEditingBloodGroupValue,
    editingTherapeuticClassId,
    setEditingTherapeuticClassId,
    editingTherapeuticClassValue,
    setEditingTherapeuticClassValue,
    editingMedicineSaltId,
    setEditingMedicineSaltId,
    editingMedicineSaltData,
    setEditingMedicineSaltData,
    editingMedicalPrescriptionId,
    setEditingMedicalPrescriptionId,
    editingMedicalPrescriptionData,
    setEditingMedicalPrescriptionData,
    editingMedicineDosageId,
    setEditingMedicineDosageId,
    editingMedicineDosageValue,
    setEditingMedicineDosageValue,
    editingPatientPrescriptionMedicineId,
    setEditingPatientPrescriptionMedicineId,
    editingPatientPrescriptionMedicineData,
    setEditingPatientPrescriptionMedicineData,
    editingPatientCaseId,
    setEditingPatientCaseId,
    editingPatientCaseData,
    setEditingPatientCaseData,
    editingPatientComplaintId,
    setEditingPatientComplaintId,
    editingPatientComplaintData,
    setEditingPatientComplaintData,
    editingCaseDiagnosisId,
    setEditingCaseDiagnosisId,
    editingCaseDiagnosisData,
    setEditingCaseDiagnosisData,
    editingPatientInvestigationId,
    setEditingPatientInvestigationId,
    editingPatientInvestigationData,
    setEditingPatientInvestigationData,
    editingProfileTopicId,
    setEditingProfileTopicId,
    editingProfileTopicValue,
    setEditingProfileTopicValue,
    editingProfileSubTopicId,
    setEditingProfileSubTopicId,
    editingProfileSubTopicData,
    setEditingProfileSubTopicData,
    editingServiceTypeId,
    setEditingServiceTypeId,
    editingServiceTypeValue,
    setEditingServiceTypeValue,
    editingMedicalServiceId,
    setEditingMedicalServiceId,
    editingMedicalServiceValue,
    setEditingMedicalServiceValue,
    editingServiceId,
    setEditingServiceId,
    editingServiceData,
    setEditingServiceData,
    editingQuestionTypeId,
    setEditingQuestionTypeId,
    editingQuestionTypeValue,
    setEditingQuestionTypeValue,
    editingQuestionId,
    setEditingQuestionId,
    editingQuestionData,
    setEditingQuestionData,
    editingOptionId,
    setEditingOptionId,
    editingOptionData,
    setEditingOptionData,
    editingQuestionGroupId,
    setEditingQuestionGroupId,
    editingQuestionGroupData,
    setEditingQuestionGroupData,
    editingEventTypeId,
    setEditingEventTypeId,
    editingEventTypeValue,
    setEditingEventTypeValue,
    editingImmunizationId,
    setEditingImmunizationId,
    editingImmunizationData,
    setEditingImmunizationData,
    editingVaccineId,
    setEditingVaccineId,
    editingVaccineData,
    setEditingVaccineData,
    editingSymptomCategoryId,
    setEditingSymptomCategoryId,
    editingSymptomCategoryValue,
    setEditingSymptomCategoryValue,
    editingSupplierTypeId,
    setEditingSupplierTypeId,
    editingSupplierTypeValue,
    setEditingSupplierTypeValue,
    editingProductCategoryId,
    setEditingProductCategoryId,
    editingProductCategoryData,
    setEditingProductCategoryData,
    editingProductSubCategoryId,
    setEditingProductSubCategoryId,
    editingProductSubCategoryData,
    setEditingProductSubCategoryData,
    editingProductId,
    setEditingProductId,
    editingProductData,
    setEditingProductData,
    editingMvtJourneyPhaseId,
    setEditingMvtJourneyPhaseId,
    editingMvtJourneyPhaseValue,
    setEditingMvtJourneyPhaseValue,
    editingMvtJourneyStepId,
    setEditingMvtJourneyStepId,
    editingMvtJourneyStepData,
    setEditingMvtJourneyStepData,
    PRODUCT_STATUS_OPTIONS,
    sidebarOpen,
    setSidebarOpen,
    expandedGroups,
    setExpandedGroups,
    expandedSections,
    setExpandedSections,
    handleLocationTypeChange,
    handleLocationChange,
    handleQuestionFormChange,
    handleEditingQuestionChange,
    makeDeleteHandler,
    handleEditUserType,
    handleSaveUserType,
    handleDeleteUserType,
    handleEditType,
    handleSaveType,
    handleDeleteType,
    handleEditLoc,
    handleSaveLoc,
    handleDeleteLoc,
    handleEditSpec,
    handleSaveSpec,
    handleDeleteSpec,
    handleEditProc,
    handleSaveProc,
    handleDeleteProc,
    handleEditDisease,
    handleSaveDisease,
    handleDeleteDisease,
    handleEditEst,
    handleSaveEst,
    handleDeleteEst,
    handleEditEstSubType,
    handleSaveEstSubType,
    handleDeleteEstSubType,
    handleEditDeg,
    handleSaveDeg,
    handleDeleteDeg,
    handleEditSpecz,
    handleSaveSpecz,
    handleDeleteSpecz,
    handleEditSpecType,
    handleSaveSpecType,
    handleDeleteSpecType,
    handleEditAccr,
    handleSaveAccr,
    handleDeleteAccr,
    handleEditDeptCat,
    handleSaveDeptCat,
    handleDeleteDeptCat,
    handleEditDept,
    handleSaveDept,
    handleDeleteDept,
    handleEditDesig,
    handleSaveDesig,
    handleDeleteDesig,
    handleEditRelationship,
    handleSaveRelationship,
    handleDeleteRelationship,
    handleEditBloodGroup,
    handleSaveBloodGroup,
    handleDeleteBloodGroup,
    handleEditTherapeuticClass,
    handleSaveTherapeuticClass,
    handleDeleteTherapeuticClass,
    handleEditMedicineSalt,
    handleSaveMedicineSalt,
    handleDeleteMedicineSalt,
    handleEditMedicalPrescription,
    handleSaveMedicalPrescription,
    handleDeleteMedicalPrescription,
    handleEditMedicineDosage,
    handleSaveMedicineDosage,
    handleDeleteMedicineDosage,
    handleEditPatientPrescriptionMedicine,
    handleSavePatientPrescriptionMedicine,
    handleDeletePatientPrescriptionMedicine,
    handleEditPatientCase,
    handleSavePatientCase,
    handleDeletePatientCase,
    handleEditPatientComplaint,
    handleSavePatientComplaint,
    handleDeletePatientComplaint,
    handleEditCaseDiagnosis,
    handleSaveCaseDiagnosis,
    handleDeleteCaseDiagnosis,
    handleEditPatientInvestigation,
    handleSavePatientInvestigation,
    handleDeletePatientInvestigation,
    handleEditSupplierType,
    handleSaveSupplierType,
    handleDeleteSupplierType,
    handleEditProductCategory,
    handleSaveProductCategory,
    handleDeleteProductCategory,
    handleEditProductSubCategory,
    handleSaveProductSubCategory,
    handleDeleteProductSubCategory,
    handleEditProduct,
    handleSaveProduct,
    handleDeleteProduct,
    handleEditMvtJourneyPhase,
    handleSaveMvtJourneyPhase,
    handleDeleteMvtJourneyPhase,
    handleEditMvtJourneyStep,
    handleSaveMvtJourneyStep,
    handleDeleteMvtJourneyStep,
    handleEditProfileTopic,
    handleSaveProfileTopic,
    handleDeleteProfileTopic,
    handleEditProfileSubTopic,
    handleSaveProfileSubTopic,
    handleDeleteProfileSubTopic,
    handleEditServiceType,
    handleSaveServiceType,
    handleDeleteServiceType,
    handleEditMedicalService,
    handleSaveMedicalService,
    handleDeleteMedicalService,
    handleEditService,
    handleSaveService,
    handleDeleteService,
    handleEditQuestionType,
    handleSaveQuestionType,
    handleDeleteQuestionType,
    handleEditQuestion,
    handleSaveQuestion,
    handleDeleteQuestion,
    handleEditOption,
    handleSaveOption,
    handleDeleteOption,
    handleEditQuestionGroup,
    handleSaveQuestionGroup,
    handleDeleteQuestionGroup,
    handleEditAggravatingFactor,
    handleSaveAggravatingFactor,
    handleDeleteAggravatingFactor,
    handleEditAllergyType,
    handleSaveAllergyType,
    handleDeleteAllergyType,
    handleEditAllergy,
    handleSaveAllergy,
    handleDeleteAllergy,
    handleEditTraumaType,
    handleSaveTraumaType,
    handleDeleteTraumaType,
    handleEditTrauma,
    handleSaveTrauma,
    handleDeleteTrauma,
    handleEditAddictionCategory,
    handleSaveAddictionCategory,
    handleDeleteAddictionCategory,
    handleEditAddictionSubCategory,
    handleSaveAddictionSubCategory,
    handleDeleteAddictionSubCategory,
    handleEditAddiction,
    handleSaveAddiction,
    handleDeleteAddiction,
    handleEditPatientCondition,
    handleSavePatientCondition,
    handleDeletePatientCondition,
    handleEditInvestigationType,
    handleSaveInvestigationType,
    handleDeleteInvestigationType,
    handleEditInvestigationGroup,
    handleSaveInvestigationGroup,
    handleDeleteInvestigationGroup,
    handleEditInvestigation,
    handleSaveInvestigation,
    handleDeleteInvestigation,
    handleEditSymptomCategory,
    handleSaveSymptomCategory,
    handleDeleteSymptomCategory,
    handleEditSymptom,
    handleSaveSymptom,
    handleDeleteSymptom,
    handleEditEstb,
    handleSaveEstb,
    handleDeleteEstb,
    handleEditDoctor,
    handleSaveDoctor,
    handleDeleteDoctor,
    handleEditPatient,
    handleCancelPatientEdit,
    handleDeletePatient,
    handleEditUser,
    handleCancelUserEdit,
    handleDeleteUser,
    showLoginModal: !!createLoginEstb || !!createLoginDoctor,
    createLoginEstb,
    createLoginDoctor,
    establishmentLoginForm,
    setEstablishmentLoginForm,
    establishmentLogins,
    doctorLoginForm,
    setDoctorLoginForm,
    doctorLogins,
    loginPasswordsById,
    savingEstablishmentLogin,
    savingDoctorLogin,
    handleOpenCreateLogin,
    handleOpenCreateDoctorLogin,
    handleOpenCreateUserLogin,
    handleCloseUserLogin,
    handleCreateLoginUserChange,
    handleUserLoginChange,
    handleSaveUserLogin,
    handleEditUserLogin,
    handleCancelUserLoginEdit,
    handleDeleteUserLogin,
    createLoginUser,
    userLoginForm,
    userLogins,
    savingUserLogin,
    handleCloseCreateLogin,
    handleCreateLoginEstablishmentChange,
    handleCreateLoginDoctorChange,
    handleEstablishmentLoginChange,
    handleDoctorLoginChange,
    handleSaveEstablishmentLogin,
    handleEditEstablishmentLogin,
    handleCancelEstablishmentLoginEdit,
    handleDeleteEstablishmentLogin,
    handleSaveDoctorLogin,
    handleEditDoctorLogin,
    handleCancelDoctorLoginEdit,
    handleDeleteDoctorLogin,
    handleSaveLocationLogin,
    handleEditLocationLogin,
    handleCancelLocationLoginEdit,
    handleDeleteLocationLogin,
    locationLoginForm,
    locationLogins,
    handleLocationLoginChange,
    savingLocationLogin,
    LOGIN_ADMIN_STEPS,
    isLoginAdminStep,
    handleEditEventType,
    handleSaveEventType,
    handleDeleteEventType,
    handleEditImmunization,
    handleSaveImmunization,
    handleDeleteImmunization,
    handleEditVaccine,
    handleSaveVaccine,
    handleDeleteVaccine,
    handleSubmit,
    renderForm,
    toggleMenuGroup,
    toggleMenuSection,
    selectStep,
    STEPS,
    STEP_DESCRIPTIONS,
    MENU_GROUPS,
    getGroupSteps,
    API_BASE,
    UPLOADS_BASE,
    formatDateTime,
    formatProcCostUsd,
    formatProcCostInr,
    parseImagePaths,
    getMorbidityDiseases,
    morbidityDiseases,
  };
}
